import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  View, Text, ScrollView, TouchableOpacity, TextInput, Image, Alert, Modal,
  FlatList, SafeAreaView, RefreshControl, Dimensions, StyleSheet, Animated,
  Share, Linking, Switch, KeyboardAvoidingView, Platform, ActivityIndicator,
} from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useFocusEffect } from '@react-navigation/native';
import ProfileManagementService from '../../services/ProfileManagementService';
import PROFILE_KEYS from '../../constants/profileKeys';
import useProfile from '../../hooks/useProfile';

// ─── Lazy image libs ───────────────────────────────────────────────────────────
let ImagePicker, ImageManipulator;
try { ImagePicker      = require('expo-image-picker');              } catch {}
try { ImageManipulator = require('expo-image-manipulator');         } catch {}

const API_BASE = 'https://api.jobeng.ai';
const API_KEY  = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

const apiRequest = async (endpoint, options = {}) => {
  try {
    const [path, qs] = endpoint.split('?');
    const dbPath = path.replace(/-/g, '_');
    const url = `${API_BASE}/api/data/${dbPath}${qs ? '?' + qs : ''}`;
    const res = await fetch(url, {
      headers: { 'Content-Type': 'application/json', 'x-api-key': API_KEY },
      ...options,
      ...(options.body ? { body: JSON.stringify(options.body) } : {}),
    });
    if (!res.ok) throw new Error(`API ${res.status}`);
    return res.json();
  } catch (e) { throw e; }
};

const { width } = Dimensions.get('window');

// ─── Shared keys (synced with InternshipManagementScreen business side) ───────
const SHARED = {
  PORTAL_APPS : 'intern_portal_applications',  // student writes; business reads & updates status
  PROGRAMS    : 'intern_programs',             // business publishes; student reads
};

// ─── Profile keys (synced with ProfileScreen + ProfessionalDashboard) ─────────
const PROF_KEYS = {
  PROFILE: 'prof_user_profile',   // full profile JSON
  DASH:    'prof_dashboard_data', // dashboard sync payload
  STATS:   'profileStats',       // { completionPercentage, profileStrength, ... }
};

// Helper: check if a string is a valid image URI (http, file, content, base64)
const isImgUri = u => u && typeof u === 'string' &&
  (u.startsWith('http') || u.startsWith('file://') || u.startsWith('content://') || u.startsWith('data:image') || u.length > 200);

// ─── DB sync helpers for Student Portal ──────────────────────────────────────
const SP_API_BASE = 'https://api.jobeng.ai';
const SP_API_KEY  = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

const spFetch = async (ep, opts={}) => {
  try {
    const token = await AsyncStorage.getItem('auth_token')||await AsyncStorage.getItem('userToken');
    const r = await fetch(`${SP_API_BASE}${ep}`, {
      headers: {'Content-Type':'application/json','x-api-key':SP_API_KEY,
        ...(token?{Authorization:`Bearer ${token}`}:{})},
      ...opts,
    });
    if (!r.ok) return {error:true,status:r.status};
    return r.json();
  } catch(e) { return {error:true,message:e.message}; }
};

const resolveStudentUid = async () => {
  for (const k of ['userId','user_id','student_user_id']) {
    const v = await AsyncStorage.getItem(k);
    if (v && !isNaN(parseInt(v))) return parseInt(v);
  }
  return null;
};

// Sync student profile (name, email, phone, university, major) → users table
const syncStudentProfileToDB = async (profile) => {
  try {
    const uid = await resolveStudentUid();
    if (!uid || !profile) return;
    const payload = {
      full_name:   profile.name || profile.fullName || '',
      email:       profile.email       || '',
      phone:       profile.phone       || '',
      bio:         profile.bio         || '',
      about:       profile.bio         || '',
      location:    profile.location    || profile.university || '',
      nationality: profile.nationality || '',
    };
    // Only send non-empty fields
    const filtered = Object.fromEntries(Object.entries(payload).filter(([,v])=>v));
    if (Object.keys(filtered).length > 0) {
      await spFetch(`/api/users/${uid}`,{method:'PUT',body:JSON.stringify(filtered)}).catch(()=>{});
    }
  } catch {}
};

// Load student profile from DB and merge into studentData
const loadStudentProfileFromDB = async (setStudentData) => {
  try {
    const uid = await resolveStudentUid();
    if (!uid) return;
    const r = await spFetch(`/api/users/${uid}/profile`);
    if (!r.error && r.data) {
      const d = r.data;
      setStudentData(prev => ({
        ...prev,
        profile: {
          ...prev.profile,
          name:        d.full_name  || prev.profile?.name       || '',
          email:       d.email      || prev.profile?.email      || '',
          phone:       d.phone      || prev.profile?.phone      || '',
          bio:         d.bio||d.about || prev.profile?.bio     || '',
          location:    d.location   || prev.profile?.location   || '',
          avatar:      d.profile_image||d.avatar || prev.profile?.avatar || null,
          // Add skills from DB
          skills:      d.skills?.map(s=>s.skill_name||s.name||'').filter(Boolean) || prev.profile?.skills || [],
          education:   d.education?.map(e=>e.institution||'').filter(Boolean) || prev.profile?.education || [],
        },
      }));
      // Persist user id
      if (d.id) {
        await AsyncStorage.setItem('userId', String(d.id));
        await AsyncStorage.setItem('user_id', String(d.id));
      }
    }
  } catch {}
};

// Sync skills added in portal → user_skills table
const syncPortalSkillsToDB = async (skills=[]) => {
  try {
    const uid = await resolveStudentUid(); if (!uid) return;
    const existing = await spFetch(`/api/users/${uid}/skills`);
    const existingNames = new Set((existing?.data||[]).map(s=>(s.skill_name||'').toLowerCase()));
    for (const skName of skills) {
      if (!skName || existingNames.has(skName.toLowerCase())) continue;
      await spFetch(`/api/users/${uid}/skills`,{
        method:'POST',
        body:JSON.stringify({skill_name:skName,skill_level:2}),
      }).catch(()=>{});
    }
  } catch {}
};
const mkId = () => `id_${Date.now()}_${Math.random().toString(36).slice(2,6)}`;
const toS  = v => v == null ? '' : String(v);

const STATUS_COLORS = {
  Submitted: '#8B5CF6', 'Under Review': '#F59E0B',
  'Interview Scheduled': '#3B82F6', Accepted: '#10B981',
  Rejected: '#EF4444', Withdrawn: '#94A3B8', Offered: '#059669',
};

const PROGRAMS = [
  {
    id:'prog_001', title:'Software Engineering Internship', company:'TechCorp',
    logo:'https://images.unsplash.com/photo-1549923746-c502d488b3ea?w=100',
    location:'Remote', type:'Virtual', duration:'12 weeks', stipend:'$2,500/month',
    department:'Engineering', matchScore:92, rating:4.8, reviews:156, applicants:245,
    applicationDeadline:'2024-11-15', startDate:'2024-12-01', posted:'2024-10-01',
    industry:'Software', companySize:'1000-5000', category:'Tech',
    description:'Join our engineering team to work on cutting-edge web applications using React, Node.js, and cloud technologies. Work alongside senior engineers on real products used by millions of users worldwide.',
    requirements:['CS or related field','JavaScript/TypeScript','React experience','RESTful APIs','Git version control'],
    responsibilities:['Develop web applications','Collaborate with senior engineers','Code reviews','Write unit tests','Attend daily standups'],
    skills:['React','Node.js','JavaScript','Git','AWS'],
    benefits:['Senior engineer mentorship','Real project experience','Certificate of completion','Potential full-time offer','Flexible hours'],
    perks:['Remote work','Health insurance','Stock options'],
  },
  {
    id:'prog_002', title:'UX Design Internship', company:'DesignHub',
    logo:'https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=100',
    location:'New York, NY', type:'Hybrid', duration:'10 weeks', stipend:'$2,200/month',
    department:'Design', matchScore:78, rating:4.9, reviews:98, applicants:189,
    applicationDeadline:'2024-11-20', startDate:'2024-12-15', posted:'2024-10-05',
    industry:'Design', companySize:'200-500', category:'Design',
    description:'Create intuitive user experiences for mobile and web applications. Conduct user research, build prototypes, and collaborate with product teams to ship delightful interfaces.',
    requirements:['Design or HCI field','Figma or Sketch proficiency','Design principles','Portfolio required','User research preferred'],
    responsibilities:['Create wireframes & prototypes','Conduct user research','Collaborate with product managers','Design UIs','Present to stakeholders'],
    skills:['Figma','User Research','Prototyping','Design Systems','Adobe XD'],
    benefits:['Design mentorship','Portfolio reviews','Conference access','Adobe CC license','Networking opportunities'],
    perks:['Hybrid work','Creative budget','Conference tickets'],
  },
  {
    id:'prog_003', title:'Data Science Internship', company:'DataTech',
    logo:'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=100',
    location:'San Francisco, CA', type:'In-Person', duration:'16 weeks', stipend:'$3,000/month',
    department:'Data Science', matchScore:85, rating:4.7, reviews:203, applicants:312,
    applicationDeadline:'2024-11-30', startDate:'2025-01-15', posted:'2024-10-08',
    industry:'Analytics', companySize:'500-1000', category:'Data',
    description:'Analyze large datasets and build ML models to drive business insights. Work alongside PhD data scientists on cutting-edge research projects with real-world impact.',
    requirements:['Statistics/Math/CS','Python programming','pandas/numpy/sklearn','SQL experience','ML fundamentals'],
    responsibilities:['Analyze complex datasets','Build predictive models','Create visualisations','Present to stakeholders','Collaborate with data engineering'],
    skills:['Python','SQL','Machine Learning','Tableau','Statistics'],
    benefits:['Cutting-edge tools','Conference attendance','Publication opportunities','PhD mentorship','Competitive stipend'],
    perks:['Free lunch','On-site gym','Learning budget'],
  },
  {
    id:'prog_004', title:'Product Management Internship', company:'ProductCo',
    logo:'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=100',
    location:'Austin, TX', type:'Hybrid', duration:'12 weeks', stipend:'$2,800/month',
    department:'Product', matchScore:70, rating:4.6, reviews:89, applicants:178,
    applicationDeadline:'2024-12-01', startDate:'2025-01-06', posted:'2024-10-12',
    industry:'SaaS', companySize:'100-200', category:'Product',
    description:'Drive product strategy for our B2B SaaS platform. Work with engineering, design, and sales to ship features that customers love. Own a real product roadmap from day one.',
    requirements:['Business/CS/related field','Analytical mindset','Excellent communication','Data-driven','Customer empathy'],
    responsibilities:['Define product requirements','Competitive analysis','User interviews','Write PRDs','Track metrics'],
    skills:['Product Strategy','Roadmapping','Analytics','User Research','Agile'],
    benefits:['PM mentorship','Real ownership','Executive exposure','PM toolkit','Return offer consideration'],
    perks:['Stock options','Home office budget','Annual retreat'],
  },
  {
    id:'prog_005', title:'Marketing Analytics Internship', company:'GrowthLab',
    logo:'https://images.unsplash.com/photo-1533750516457-a7f992034fec?w=100',
    location:'Remote', type:'Virtual', duration:'8 weeks', stipend:'$1,800/month',
    department:'Marketing', matchScore:65, rating:4.5, reviews:67, applicants:134,
    applicationDeadline:'2024-12-10', startDate:'2025-02-01', posted:'2024-10-15',
    industry:'Growth', companySize:'50-100', category:'Marketing',
    description:'Leverage data to optimise marketing campaigns and drive customer acquisition. Use Google Analytics, Mixpanel, and SQL to uncover growth opportunities with a global team.',
    requirements:['Marketing/Statistics/related','Google Analytics','Excel expert','Basic SQL helpful','Creative problem solving'],
    responsibilities:['Analyse campaigns','A/B test pages','Build dashboards','Optimise ad spend','Report to CMO weekly'],
    skills:['Google Analytics','SQL','Excel','A/B Testing','SEO'],
    benefits:['Marketing toolkit','Campaign budget','Performance bonus','Flexible hours','International exposure'],
    perks:['100% remote','Global team','Results bonus'],
  },
];

const COMPANIES = [
  { name:'TechCorp',  rating:4.8, openings:12, logo:'https://images.unsplash.com/photo-1549923746-c502d488b3ea?w=100' },
  { name:'DesignHub', rating:4.9, openings:5,  logo:'https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=100' },
  { name:'DataTech',  rating:4.7, openings:8,  logo:'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=100' },
  { name:'ProductCo', rating:4.6, openings:3,  logo:'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=100' },
];

const DEFAULT_STUDENT = {
  profile: {
    id:'student_001', name:'Alex Johnson', email:'alex.johnson@university.edu',
    phone:'+1-555-0123', university:'Tech University', major:'Computer Science',
    year:'Junior', gpa:'3.8', bio:'Passionate CS student with a love for building products.',
    avatar:'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150',
    skills:['JavaScript','React','Python','Git','SQL'],
    interests:['Web Development','Mobile Apps','AI/ML'],
    resume:'resume.pdf', portfolio:'https://alexjohnson.dev',
    linkedin:'https://linkedin.com/in/alexjohnson', github:'https://github.com/alexjohnson',
    availability:'December 2024', completeness:72,
  },
  applications: [
    { id:'app_001', programId:'prog_001', programTitle:'Software Engineering Internship', company:'TechCorp',
      status:'Under Review', appliedDate:'2024-10-15', interviewDate:'2024-10-25', interviewType:'Virtual',
      progress:60, nextStep:'Technical Interview', feedback:'Strong technical background. Proceed to technical round.',
      documents:['Resume','Cover Letter','Transcript'], coverLetter:'', rating:null },
    { id:'app_002', programId:'prog_002', programTitle:'UX Design Internship', company:'DesignHub',
      status:'Accepted', appliedDate:'2024-10-10', acceptedDate:'2024-10-20', startDate:'2024-11-01',
      progress:100, nextStep:'Onboarding', feedback:'Excellent portfolio and design thinking skills.',
      documents:['Resume','Portfolio','Design Challenge'], rating:5 },
  ],
  savedPrograms: ['prog_003','prog_004'],
  notifications: [
    { id:'n1', type:'interview', title:'Interview Scheduled', message:'Your interview for Software Engineering is scheduled for Oct 25.', date:'2024-10-20', read:false },
    { id:'n2', type:'acceptance', title:'Application Accepted! \uD83C\uDF89', message:'Congratulations! You have been accepted for the UX Design Internship.', date:'2024-10-20', read:false },
    { id:'n3', type:'deadline', title:'Deadline Reminder', message:"DataTech application deadline is in 3 days. Don't miss it!", date:'2024-10-22', read:true },
  ],
  achievements: [
    { id:'ach1', title:'First Application', icon:'rocket-outline', color:'#667eea', earned:true },
    { id:'ach2', title:'Profile Complete',  icon:'person-outline', color:'#10B981', earned:false },
    { id:'ach3', title:'Interview Ready',   icon:'mic-outline',    color:'#F59E0B', earned:false },
    { id:'ach4', title:'Saved 5',           icon:'bookmark',       color:'#DB2777', earned:false },
  ],
};

// ─────────────────────────────────────────────────────────────────────────────
export default function StudentInternshipPortalScreen({ navigation }) {
  // ── Profile integration (auto-injected) ──
  const profileCtx = useProfile();
  const [activeTab, setActiveTab]   = useState('discover');
  const [search,    setSearch]      = useState('');
  const [sortBy,    setSortBy]      = useState('match');
  const [viewMode,  setViewMode]    = useState('card');
  const [refreshing, setRefreshing] = useState(false);
  const [savingForm, setSavingForm] = useState(false);

  const [filters, setFilters] = useState({
    type:'All', department:'All', location:'All', remote:false, matchMin:0,
  });

  const [studentData, setStudentData] = useState(DEFAULT_STUDENT);

  const [showModal,    setShowModal]    = useState(false);
  const [modalType,    setModalType]    = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [showFilters,  setShowFilters]  = useState(false);
  const [compareList,  setCompareList]  = useState([]);
  const [showCompare,  setShowCompare]  = useState(false);

  const [appForm, setAppForm] = useState({ coverLetter:'', whyInterested:'', relevantExperience:'', availability:'', additionalInfo:'' });
  const [editProfile, setEditProfile] = useState({});

  const fadeAnim = useRef(new Animated.Value(0)).current;

  // ── Image editor state ────────────────────────────────────────────────────
  const [showImageEditor, setShowImageEditor] = useState(false);
  const [editorSource,    setEditorSource]    = useState(null); // raw picked uri
  const [editedAvatar,    setEditedAvatar]    = useState(null); // final edited uri
  const [imgEdits, setImgEdits] = useState({ rotate:0, flipH:false, flipV:false, brightness:1.0 });
  const [imgPreview, setImgPreview] = useState(null); // live preview uri
  const [editingImg, setEditingImg] = useState(false);

  useEffect(() => {
    loadData();
    loadPublishedPrograms();
    syncApplicationStatuses();
    Animated.timing(fadeAnim, { toValue:1, duration:600, useNativeDriver:true }).start();
    // Load profile from real DB and merge into student data
    loadStudentProfileFromDB(setStudentData).catch(()=>{});
  }, []);

  // Reload ProfileScreen data every time this screen comes into focus
  // (picks up photo changes, name edits, skill updates from ProfileScreen)
  useFocusEffect(
    useCallback(() => {
      loadProfileScreenData().catch(()=>{});
      syncApplicationStatuses();
    }, [])
  );

  const safeMerge = (incoming) => {
    if (!incoming || typeof incoming !== 'object') return;
    setStudentData(prev => ({
      ...prev, ...incoming,
      profile:       { ...prev.profile,       ...(incoming.profile || {}) },
      applications:  Array.isArray(incoming.applications)  ? incoming.applications  : prev.applications,
      savedPrograms: Array.isArray(incoming.savedPrograms) ? incoming.savedPrograms : prev.savedPrograms,
      notifications: Array.isArray(incoming.notifications) ? incoming.notifications : prev.notifications,
      achievements:  Array.isArray(incoming.achievements)  ? incoming.achievements  : prev.achievements,
    }));
  };

  // ── Load business-published programs (merges with static PROGRAMS) ──────────
  const [publishedPrograms, setPublishedPrograms] = useState([]);

  const loadPublishedPrograms = async () => {
    try {
      const raw = await AsyncStorage.getItem(SHARED.PROGRAMS);
      if (raw) {
        const list = JSON.parse(raw);
        if (Array.isArray(list) && list.length > 0) {
          setPublishedPrograms(list.filter(p => p.published));
        }
      }
    } catch {}
  };

  // Sync application statuses from business decisions
  const syncApplicationStatuses = async () => {
    try {
      const raw = await AsyncStorage.getItem(SHARED.PORTAL_APPS);
      if (!raw) return;
      const businessApps = JSON.parse(raw);
      if (!Array.isArray(businessApps)) return;

      setStudentData(prev => {
        const prevApps = prev.applications || [];
        let changed = false;
        const merged = prevApps.map(myApp => {
          const businessVersion = businessApps.find(ba => ba.id === myApp.id);
          if (businessVersion && businessVersion.status !== myApp.status) {
            changed = true;
            // If status changed, create a notification
            const notifId = 'notif_' + Date.now() + '_' + myApp.id;
            const notifMessages = {
              'Accepted': `🎉 Congratulations! You've been accepted for ${myApp.programTitle}!`,
              'Rejected': `Your application for ${myApp.programTitle} was not successful this time.`,
              'Interview Scheduled': `📅 Interview scheduled for ${myApp.programTitle} on ${businessVersion.interviewDate} at ${businessVersion.interviewTime}.`,
              'Shortlisted': `⭐ You've been shortlisted for ${myApp.programTitle}!`,
              'Under Review': `Your application for ${myApp.programTitle} is now under review.`,
              'More Info Requested': `The recruiter for ${myApp.programTitle} has requested more information.`,
            };
            if (notifMessages[businessVersion.status]) {
              prev.notifications = [
                { id: notifId, type: businessVersion.status === 'Accepted' ? 'acceptance' : businessVersion.status === 'Interview Scheduled' ? 'interview' : 'update',
                  title: businessVersion.status, message: notifMessages[businessVersion.status],
                  date: new Date().toISOString(), read: false },
                ...(prev.notifications || []),
              ];
            }
            return { ...myApp, ...businessVersion };
          }
          return myApp;
        });
        return changed ? { ...prev, applications: merged } : prev;
      });
    } catch (e) { console.warn('syncApplicationStatuses:', e?.message); }
  };

  // ── Image picker ────────────────────────────────────────────────────────────
  const pickProfileImage = async () => {
    if (!ImagePicker) {
      Alert.alert('Install required', 'npx expo install expo-image-picker expo-image-manipulator');
      return;
    }
    try {
      const perm = await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (!perm.granted) {
        // Try camera
        const camPerm = await ImagePicker.requestCameraPermissionsAsync();
        if (!camPerm.granted) { Alert.alert('Permission denied', 'Camera and library access are required.'); return; }
      }
      Alert.alert('Choose Photo', 'Select source', [
        { text: 'Camera', onPress: () => launchPicker('camera') },
        { text: 'Gallery', onPress: () => launchPicker('library') },
        { text: 'Cancel', style: 'cancel' },
      ]);
    } catch (e) { console.warn('pickProfileImage:', e?.message); }
  };

  const launchPicker = async (source) => {
    try {
      const opts = { mediaTypes: ['images'], allowsEditing: false, quality: 0.9 };
      const result = source === 'camera'
        ? await ImagePicker.launchCameraAsync(opts)
        : await ImagePicker.launchImageLibraryAsync(opts);
      if (!result.canceled && result.assets?.[0]) {
        const uri = result.assets[0].uri;
        setEditorSource(uri);
        setImgPreview(uri);
        setImgEdits({ rotate: 0, flipH: false, flipV: false, brightness: 1.0 });
        setShowImageEditor(true);
      }
    } catch (e) { console.warn('launchPicker:', e?.message); }
  };

  // Apply edits via ImageManipulator and update preview
  const applyEditsPreview = async (edits) => {
    if (!editorSource || !ImageManipulator) return;
    setEditingImg(true);
    try {
      const actions = [];
      if (edits.rotate !== 0)  actions.push({ rotate: edits.rotate });
      if (edits.flipH)         actions.push({ flip: ImageManipulator.FlipType.Horizontal });
      if (edits.flipV)         actions.push({ flip: ImageManipulator.FlipType.Vertical });
      const result = await ImageManipulator.manipulateAsync(
        editorSource, actions,
        { compress: 0.85, format: ImageManipulator.SaveFormat.JPEG }
      );
      setImgPreview(result.uri);
    } catch (e) { console.warn('applyEditsPreview:', e?.message); }
    finally { setEditingImg(false); }
  };

  const handleEditChange = (key, val) => {
    const next = { ...imgEdits, [key]: val };
    setImgEdits(next);
    applyEditsPreview(next);
  };

  const confirmImageEdit = async () => {
    if (!editorSource || !ImageManipulator) {
      if (editorSource) saveAvatarUri(editorSource);
      setShowImageEditor(false);
      return;
    }
    setEditingImg(true);
    try {
      const actions = [];
      if (imgEdits.rotate !== 0)  actions.push({ rotate: imgEdits.rotate });
      if (imgEdits.flipH)         actions.push({ flip: ImageManipulator.FlipType.Horizontal });
      if (imgEdits.flipV)         actions.push({ flip: ImageManipulator.FlipType.Vertical });
      // Resize to square 400×400 for avatar
      actions.push({ resize: { width: 400, height: 400 } });
      const result = await ImageManipulator.manipulateAsync(
        editorSource, actions,
        { compress: 0.85, format: ImageManipulator.SaveFormat.JPEG }
      );
      saveAvatarUri(result.uri);
    } catch (e) {
      console.warn('confirmImageEdit:', e?.message);
      if (editorSource) saveAvatarUri(editorSource);
    }
    finally { setEditingImg(false); setShowImageEditor(false); }
  };

  const saveAvatarUri = (uri) => {
    setEditedAvatar(uri);
    const updated = { ...studentData, profile: { ...studentData.profile, avatar: uri } };
    setStudentData(updated);
    persistData(updated);

    // Sync photo to ProfileScreen keys so it shows on Dashboard and Profile
    AsyncStorage.setItem('profile_photo_uri', uri).catch(()=>{});
    // Also update prof_user_profile
    AsyncStorage.getItem(PROF_KEYS.PROFILE).then(raw => {
      const prof = raw ? JSON.parse(raw) : { personal:{} };
      prof.personal = { ...(prof.personal||{}), profileImage: uri };
      AsyncStorage.setItem(PROF_KEYS.PROFILE, JSON.stringify(prof)).catch(()=>{});
      AsyncStorage.setItem('userProfile', JSON.stringify(prof)).catch(()=>{});
    }).catch(()=>{});
  };

  // ── Image Editor Modal ─────────────────────────────────────────────────────
  const renderImageEditor = () => (
    <Modal visible={showImageEditor} animationType="slide" presentationStyle="fullScreen"
      onRequestClose={() => setShowImageEditor(false)}>
      <SafeAreaView style={{ flex:1, backgroundColor:'#0F172A' }}>
        {/* Editor Header */}
        <View style={{ flexDirection:'row', alignItems:'center', paddingHorizontal:16, paddingVertical:12, borderBottomWidth:1, borderBottomColor:'#1E293B' }}>
          <TouchableOpacity style={{ width:36, height:36, borderRadius:10, backgroundColor:'#1E293B', justifyContent:'center', alignItems:'center' }}
            onPress={() => setShowImageEditor(false)}>
            <Ionicons name="close" size={20} color="#94A3B8"/>
          </TouchableOpacity>
          <Text style={{ flex:1, textAlign:'center', fontSize:16, fontWeight:'700', color:'#F1F5F9' }}>Edit Photo</Text>
          <TouchableOpacity style={{ backgroundColor:'#667eea', borderRadius:10, paddingHorizontal:16, paddingVertical:8 }}
            onPress={confirmImageEdit} disabled={editingImg}>
            {editingImg
              ? <ActivityIndicator size={14} color="#FFF"/>
              : <Text style={{ color:'#FFF', fontWeight:'700', fontSize:14 }}>Done</Text>
            }
          </TouchableOpacity>
        </View>

        {/* Preview */}
        <View style={{ flex:1, justifyContent:'center', alignItems:'center', backgroundColor:'#0F172A', padding:20 }}>
          {imgPreview ? (
            <View style={{ position:'relative' }}>
              <Image source={{ uri: imgPreview }}
                style={{ width: width - 40, height: width - 40, borderRadius: 16, backgroundColor:'#1E293B' }}
                resizeMode="contain"/>
              {editingImg && (
                <View style={{ position:'absolute', inset:0, justifyContent:'center', alignItems:'center', backgroundColor:'rgba(0,0,0,0.4)', borderRadius:16 }}>
                  <ActivityIndicator size={32} color="#667eea"/>
                  <Text style={{ color:'#FFF', marginTop:8, fontSize:13 }}>Processing…</Text>
                </View>
              )}
            </View>
          ) : (
            <View style={{ width:width-40, height:width-40, borderRadius:16, backgroundColor:'#1E293B', justifyContent:'center', alignItems:'center' }}>
              <Ionicons name="image-outline" size={64} color="#475569"/>
            </View>
          )}
        </View>

        {/* Controls */}
        <View style={{ backgroundColor:'#1E293B', borderTopLeftRadius:20, borderTopRightRadius:20, padding:20 }}>

          {/* Rotate buttons */}
          <View style={{ marginBottom:20 }}>
            <Text style={{ fontSize:12, fontWeight:'600', color:'#94A3B8', marginBottom:10, textTransform:'uppercase', letterSpacing:1 }}>Rotate & Flip</Text>
            <View style={{ flexDirection:'row', gap:10 }}>
              {[
                { label:'↺ 90°', onPress:() => handleEditChange('rotate', ((imgEdits.rotate - 90) + 360) % 360) },
                { label:'↻ 90°', onPress:() => handleEditChange('rotate', (imgEdits.rotate + 90) % 360) },
                { label:'↔ Flip H', onPress:() => handleEditChange('flipH', !imgEdits.flipH), active: imgEdits.flipH },
                { label:'↕ Flip V', onPress:() => handleEditChange('flipV', !imgEdits.flipV), active: imgEdits.flipV },
              ].map((btn, i) => (
                <TouchableOpacity key={i}
                  style={{ flex:1, paddingVertical:10, borderRadius:10, backgroundColor: btn.active ? '#667eea' : '#334155', alignItems:'center' }}
                  onPress={btn.onPress}>
                  <Text style={{ color: btn.active ? '#FFF' : '#94A3B8', fontSize:12, fontWeight:'600' }}>{btn.label}</Text>
                </TouchableOpacity>
              ))}
            </View>
            {imgEdits.rotate !== 0 && (
              <Text style={{ fontSize:11, color:'#667eea', textAlign:'center', marginTop:6 }}>
                Rotated {imgEdits.rotate}°
              </Text>
            )}
          </View>

          {/* Brightness control */}
          <View style={{ marginBottom:20 }}>
            <View style={{ flexDirection:'row', justifyContent:'space-between', marginBottom:10 }}>
              <Text style={{ fontSize:12, fontWeight:'600', color:'#94A3B8', textTransform:'uppercase', letterSpacing:1 }}>Brightness</Text>
              <Text style={{ fontSize:12, fontWeight:'700', color:'#667eea' }}>{Math.round(imgEdits.brightness * 100)}%</Text>
            </View>
            <View style={{ flexDirection:'row', gap:8 }}>
              {[0.5, 0.75, 1.0, 1.25, 1.5].map(v => (
                <TouchableOpacity key={v}
                  style={{ flex:1, paddingVertical:8, borderRadius:8, backgroundColor: Math.abs(imgEdits.brightness - v) < 0.1 ? '#667eea' : '#334155', alignItems:'center' }}
                  onPress={() => handleEditChange('brightness', v)}>
                  <Text style={{ color: Math.abs(imgEdits.brightness - v) < 0.1 ? '#FFF' : '#94A3B8', fontSize:11, fontWeight:'600' }}>
                    {v === 0.5 ? '50%' : v === 0.75 ? '75%' : v === 1.0 ? 'Normal' : v === 1.25 ? '125%' : '150%'}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>

          {/* Aspect ratio / crop presets */}
          <View style={{ marginBottom:8 }}>
            <Text style={{ fontSize:12, fontWeight:'600', color:'#94A3B8', marginBottom:10, textTransform:'uppercase', letterSpacing:1 }}>Crop Shape</Text>
            <View style={{ flexDirection:'row', gap:10 }}>
              {[{ label:'Square', icon:'square-outline' }, { label:'Circle', icon:'ellipse-outline' }, { label:'Reset', icon:'refresh-outline' }].map((opt, i) => (
                <TouchableOpacity key={i}
                  style={{ flex:1, paddingVertical:10, borderRadius:10, backgroundColor:'#334155', alignItems:'center', gap:4 }}
                  onPress={() => {
                    if (opt.label === 'Reset') {
                      const reset = { rotate:0, flipH:false, flipV:false, brightness:1.0 };
                      setImgEdits(reset);
                      setImgPreview(editorSource);
                    }
                  }}>
                  <Ionicons name={opt.icon} size={18} color="#94A3B8"/>
                  <Text style={{ color:'#94A3B8', fontSize:11, fontWeight:'600' }}>{opt.label}</Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>
        </View>
      </SafeAreaView>
    </Modal>
  );

  const loadData = async () => {
    try {
      // 1. Load from API
      try {
        const r = await apiRequest('internship-programs');
        if (r && (r.success || r.data)) safeMerge(r.data || r);
      } catch {
        const raw = await AsyncStorage.getItem('studentData');
        if (raw) safeMerge(JSON.parse(raw));
      }

      // 2. Load ProfileScreen data and merge into student profile
      await loadProfileScreenData();
    } catch {}
  };

  // ── Load profile data from ProfileScreen (same keys it writes) ────────────
  const loadProfileScreenData = async () => {
    try {
      // Read full profile from ProfileScreen
      const profileRaw = await AsyncStorage.getItem(PROF_KEYS.PROFILE) ||
                         await AsyncStorage.getItem('userProfile');
      if (profileRaw) {
        const prof = JSON.parse(profileRaw);
        if (prof?.personal) {
          const p = prof.personal;
          setStudentData(prev => ({
            ...prev,
            profile: {
              ...prev.profile,
              name:     p.fullName || prev.profile?.name     || '',
              email:    p.email    || prev.profile?.email    || '',
              phone:    p.phone    || prev.profile?.phone    || '',
              bio:      p.about    || prev.profile?.bio      || '',
              location: p.location || prev.profile?.location || '',
              university: prev.profile?.university || p.company || '',
              linkedin: p.linkedin || prev.profile?.linkedin || '',
              github:   p.github   || prev.profile?.github   || '',
              portfolio: p.website || prev.profile?.portfolio || '',
              // Photo: prefer base64 for persistence, then URI
              avatar:   p.profileImageB64 || p.profileImage || prev.profile?.avatar || null,
              // Merge skills from ProfileScreen
              skills:   (prof.skills && prof.skills.length > 0)
                ? [...new Set([
                    ...prof.skills.map(s => s.name || s.skill_name || '').filter(Boolean),
                    ...(prev.profile?.skills || []),
                  ])]
                : prev.profile?.skills || [],
            },
          }));
        }
      }

      // Read completion/strength stats
      const statsRaw = await AsyncStorage.getItem(PROF_KEYS.STATS);
      if (statsRaw) {
        const ps = JSON.parse(statsRaw);
        const comp = ps.completionPercentage || ps.completion_percentage || 0;
        if (comp > 0) {
          setStudentData(prev => ({
            ...prev,
            profile: { ...prev.profile, completeness: comp },
          }));
        }
      }

      // Read standalone photo keys (written by ProfileScreen & BuildResume)
      const [photoUri, photoB64] = await Promise.all([
        AsyncStorage.getItem('profile_photo_uri'),
        AsyncStorage.getItem('profile_photo_b64'),
      ]);
      if (photoB64 || photoUri) {
        setStudentData(prev => ({
          ...prev,
          profile: {
            ...prev.profile,
            avatar: photoB64
              ? (photoB64.startsWith('data:') ? photoB64 : `data:image/jpeg;base64,${photoB64}`)
              : photoUri || prev.profile?.avatar,
          },
        }));
      }
    } catch (e) { console.warn('loadProfileScreenData:', e?.message); }
  };

  const persistData = (data) => {
    AsyncStorage.setItem('studentData', JSON.stringify(data)).catch(() => {});
    // Sync profile fields to real DB (fire-and-forget)
    if (data?.profile) syncStudentProfileToDB(data.profile).catch(()=>{});
    // Sync skills to user_skills table
    if (data?.profile?.skills?.length>0) syncPortalSkillsToDB(data.profile.skills).catch(()=>{});
    apiRequest('internship-programs', { method:'PUT', body:data }).catch(() => {});

    // ── Sync back to ProfileScreen & Dashboard keys ──
    if (data?.profile) {
      const p = data.profile;
      const comp = p.completeness || 0;

      // Update PROF_KEYS.DASH so Dashboard picks up changes
      AsyncStorage.getItem(PROF_KEYS.DASH).then(raw => {
        const dash = raw ? JSON.parse(raw) : {};
        const updated = {
          ...dash,
          user: {
            ...(dash.user || {}),
            fullName:     p.name     || dash.user?.fullName     || '',
            email:        p.email    || dash.user?.email        || '',
            jobTitle:     p.major    || dash.user?.jobTitle     || '',
            location:     p.location || p.university || dash.user?.location || '',
            profileImage: p.avatar   || dash.user?.profileImage || null,
          },
          stats: {
            ...(dash.stats || {}),
            completionPercentage: comp,
            completion_percentage: comp,
          },
          lastUpdated: new Date().toISOString(),
        };
        AsyncStorage.setItem(PROF_KEYS.DASH, JSON.stringify(updated)).catch(()=>{});
        AsyncStorage.setItem('dashboardData', JSON.stringify(updated)).catch(()=>{});
      }).catch(()=>{});

      // Update profileStats
      AsyncStorage.getItem(PROF_KEYS.STATS).then(raw => {
        const stats = raw ? JSON.parse(raw) : {};
        stats.completionPercentage = comp;
        stats.lastUpdated = new Date().toISOString();
        AsyncStorage.setItem(PROF_KEYS.STATS, JSON.stringify(stats)).catch(()=>{});
      }).catch(()=>{});

      // Update prof_user_profile personal fields
      AsyncStorage.getItem(PROF_KEYS.PROFILE).then(raw => {
        const prof = raw ? JSON.parse(raw) : { personal:{} };
        prof.personal = {
          ...(prof.personal || {}),
          fullName:     p.name     || prof.personal?.fullName     || '',
          email:        p.email    || prof.personal?.email        || '',
          phone:        p.phone    || prof.personal?.phone        || '',
          about:        p.bio      || prof.personal?.about        || '',
          location:     p.location || prof.personal?.location     || '',
          linkedin:     p.linkedin || prof.personal?.linkedin     || '',
          github:       p.github   || prof.personal?.github       || '',
          website:      p.portfolio|| prof.personal?.website      || '',
          profileImage: p.avatar   || prof.personal?.profileImage || null,
        };
        // Merge skills
        if (p.skills?.length > 0) {
          const existingSkills = (prof.skills || []).map(s => s.name || s.skill_name || '');
          const newSkills = p.skills.filter(s => !existingSkills.includes(s));
          prof.skills = [
            ...(prof.skills || []),
            ...newSkills.map(s => ({ id: `portal_${Date.now()}_${Math.random().toString(36).slice(2,5)}`, name: s, level: 'Intermediate' })),
          ];
        }
        const profileJson = JSON.stringify(prof);
        AsyncStorage.setItem(PROF_KEYS.PROFILE, profileJson).catch(()=>{});
        AsyncStorage.setItem('userProfile', profileJson).catch(()=>{});
      }).catch(()=>{});
    }
  };

  const onRefresh = useCallback(() => {
    setRefreshing(true);
    Promise.all([loadData(), loadProfileScreenData()]).finally(() => setRefreshing(false));
  }, []);

  // ── Derived ─────────────────────────────────────────────────────────────────
  const appliedIds = new Set((studentData.applications || []).map(a => a.programId));
  const savedIds   = new Set(studentData.savedPrograms || []);
  const unread     = (studentData.notifications || []).filter(n => !n.read).length;

  const filteredPrograms = useCallback(() => {
    const q = search.toLowerCase();
    // Merge static programs with business-published programs (dedup by id)
    const staticIds = new Set(PROGRAMS.map(p => p.id));
    const extra = publishedPrograms.filter(p => !staticIds.has(p.id));
    const allPrograms = [...PROGRAMS, ...extra];
    return allPrograms
      .filter(p => {
        if (q && !String(p.title ?? '').toLowerCase().includes(q) && !String(p.company ?? '').toLowerCase().includes(q)
            && !(p.skills||[]).some(s=>s.toLowerCase().includes(q))) return false;
        if (filters.type !== 'All' && p.type !== filters.type) return false;
        if (filters.department !== 'All' && p.department !== filters.department) return false;
        if (filters.remote && p.type !== 'Virtual') return false;
        return true;
      })
      .sort((a,b) => {
        if (sortBy==='match')   return (b.matchScore||0)-(a.matchScore||0);
        if (sortBy==='rating')  return (b.rating||0)-(a.rating||0);
        if (sortBy==='stipend') return parseInt(b.stipend||0)-parseInt(a.stipend||0);
        return new Date(b.posted)-new Date(a.posted);
      });
  }, [search, filters, sortBy])();

  const fmt = d => { try { return new Date(d).toLocaleDateString('en-US',{year:'numeric',month:'short',day:'numeric'}); } catch { return d||''; } };
  const daysLeft = d => Math.ceil((new Date(d)-new Date())/(1000*60*60*24));
  const sc = s => STATUS_COLORS[s]||'#94A3B8';

  // ── Actions ─────────────────────────────────────────────────────────────────
  const toggleSave = (id) => {
    const next = savedIds.has(id) ? [...savedIds].filter(x=>x!==id) : [...savedIds, id];
    const up = { ...studentData, savedPrograms:next };
    setStudentData(up); persistData(up);
  };

  const openApply = (prog) => {
    if (appliedIds.has(prog.id)) { Alert.alert('Already Applied','You have already applied.'); return; }
    setSelectedItem(prog);
    setAppForm({ coverLetter:'', whyInterested:'', relevantExperience:'', availability:'', additionalInfo:'' });
    setModalType('apply'); setShowModal(true);
  };

  const submitApp = async () => {
    if (!appForm.coverLetter.trim()||!appForm.whyInterested.trim()||!appForm.availability.trim()) {
      Alert.alert('Required','Please fill Cover Letter, Why Interested, and Availability.'); return;
    }
    setSavingForm(true);
    try {
      const newApp = {
        id:mkId(), programId:selectedItem.id, programTitle:selectedItem.title,
        company:selectedItem.company, status:'Submitted',
        appliedDate:new Date().toISOString().split('T')[0],
        progress:10, nextStep:'Initial Review',
        feedback:'Application submitted successfully.', documents:['Resume','Cover Letter'],
        ...appForm,
      };
      const up = { ...studentData, applications:[...(studentData.applications||[]), newApp] };
      // Sync internship skills to DB
      if (program?.skills?.length>0) syncPortalSkillsToDB(program.skills).catch(()=>{});
      setStudentData(up); persistData(up); setShowModal(false);

      // ⭐ Write to shared portal applications so business side can process it
      try {
        const raw  = await AsyncStorage.getItem(SHARED.PORTAL_APPS);
        const list = raw ? JSON.parse(raw) : [];
        const portalApp = {
          ...newApp,
          studentName:       studentData.profile.name,
          studentEmail:      studentData.profile.email,
          studentPhone:      studentData.profile.phone,
          studentUniversity: studentData.profile.university,
          studentMajor:      studentData.profile.major,
          studentGpa:        studentData.profile.gpa,
          studentSkills:     studentData.profile.skills || [],
          submittedAt:       new Date().toISOString(),
          source:            'student_portal',
        };
        list.unshift(portalApp);
        await AsyncStorage.setItem(SHARED.PORTAL_APPS, JSON.stringify(list));
      } catch {}
      Alert.alert('\uD83C\uDF89 Applied!',`Application to ${selectedItem.title} submitted successfully.`);
    } catch { Alert.alert('Error','Failed to submit. Please try again.'); }
    finally { setSavingForm(false); }
  };

  const withdrawApp = (app) => {
    Alert.alert('Withdraw','Remove your application to '+app.programTitle+'?',[
      {text:'Cancel',style:'cancel'},
      {text:'Withdraw',style:'destructive',onPress:()=>{
        const up = { ...studentData, applications:(studentData.applications||[]).map(a=>a.id===app.id?{...a,status:'Withdrawn',progress:0}:a) };
        setStudentData(up); persistData(up); setShowModal(false);
      }},
    ]);
  };

  const markAllRead = () => {
    const up = { ...studentData, notifications:(studentData.notifications||[]).map(n=>({...n,read:true})) };
    setStudentData(up); persistData(up);
  };

  const markOneRead = (id) => {
    const up = { ...studentData, notifications:(studentData.notifications||[]).map(n=>n.id===id?{...n,read:true}:n) };
    setStudentData(up);
  };

  const saveProfile = async () => {
    setSavingForm(true);
    try {
      const merged = { ...studentData.profile, ...editProfile };

      // Enhanced completeness: weight more profile fields
      const checks = [
        !!merged.name,
        !!merged.email,
        !!merged.bio && merged.bio.length > 20,
        !!merged.portfolio || !!merged.linkedin,
        !!merged.linkedin,
        !!merged.avatar,
        (merged.skills||[]).length >= 3,
        (merged.interests||[]).length >= 2,
        !!merged.university,
        !!merged.phone,
      ];
      const filled = checks.filter(Boolean).length;
      merged.completeness = Math.min(100, Math.round((filled / checks.length) * 100));

      const up = { ...studentData, profile: merged };
      setStudentData(up);
      persistData(up);
      setShowModal(false);
      Alert.alert('\u2705 Saved','Profile updated successfully.');
    } catch { Alert.alert('Error','Could not save profile.'); }
    finally { setSavingForm(false); }
  };

  const toggleCompare = (p) => {
    setCompareList(prev=>{
      if (prev.find(x=>x.id===p.id)) return prev.filter(x=>x.id!==p.id);
      if (prev.length>=3) { Alert.alert('Limit','Compare up to 3 internships.'); return prev; }
      return [...prev,p];
    });
  };

  const shareProgram = async (p) => {
    try { await Share.share({ title:p.title, message:`${p.title} at ${p.company} – ${p.stipend} · ${p.location}` }); } catch {}
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // RENDER: Header
  // ─────────────────────────────────────────────────────────────────────────────
  const renderHeader = () => (
    <LinearGradient colors={['#667eea','#764ba2']} style={st.header}>
      <SafeAreaView>
        <View style={st.hdrRow}>
          <TouchableOpacity style={st.hdrBtn} onPress={()=>navigation.goBack()}>
            <Ionicons name="arrow-back" size={22} color="#FFF"/>
          </TouchableOpacity>
          <View style={{flex:1,alignItems:'center'}}>
            <Text style={st.hdrTitle}>
              Internship Portal
            </Text>
            <Text style={st.hdrSub}>{filteredPrograms.length} opportunities available</Text>
          </View>
          <TouchableOpacity style={st.hdrBtn} onPress={()=>{setModalType('notifications');setShowModal(true);}}>
            <Ionicons name="notifications" size={22} color="#FFF"/>
            {unread>0&&<View style={st.badge}><Text style={st.badgeT}>{unread}</Text></View>}
          </TouchableOpacity>
        </View>
        <View style={st.compRow}>
          <Text style={st.compLabel}>Profile {studentData.profile.completeness}% complete</Text>
          <View style={st.compBar}><View style={[st.compFill,{width:`${studentData.profile.completeness}%`}]}/></View>
        </View>
      </SafeAreaView>
    </LinearGradient>
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // RENDER: Tab Bar
  // ─────────────────────────────────────────────────────────────────────────────
  const renderTabBar = () => {
    const tabs = [
      {key:'discover',     label:'Discover',     icon:'search',        badge:null},
      {key:'applications', label:'Applications', icon:'document-text', badge:(studentData.applications||[]).filter(a=>a.status!=='Withdrawn').length||null},
      {key:'saved',        label:'Saved',        icon:'bookmark',      badge:savedIds.size||null},
      {key:'profile',      label:'Profile',      icon:'person',        badge:null},
      {key:'analytics',    label:'Insights',     icon:'analytics',     badge:null},
    ];
    return (
      <View style={st.tabBar}>
        {tabs.map(tab=>(
          <TouchableOpacity key={tab.key} style={[st.tabItem,activeTab===tab.key&&st.tabActive]} onPress={()=>setActiveTab(tab.key)}>
            <View style={{position:'relative'}}>
              <Ionicons name={tab.icon} size={20} color={activeTab===tab.key?'#667eea':'#8E8E93'}/>
              {tab.badge?<View style={st.tabBadge}><Text style={st.tabBadgeT}>{tab.badge}</Text></View>:null}
            </View>
            <Text style={[st.tabText,activeTab===tab.key&&st.tabTextActive]}>{tab.label}</Text>
          </TouchableOpacity>
        ))}
      </View>
    );
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // RENDER: Discover tab
  // ─────────────────────────────────────────────────────────────────────────────
  const renderDiscover = () => (
    <Animated.View style={[st.content,{opacity:fadeAnim}]}>
      {/* Search */}
      <View style={st.searchRow}>
        <View style={st.searchBox}>
          <Ionicons name="search" size={18} color="#8E8E93"/>
          <TextInput style={st.searchIn} placeholder="Search roles, companies, skills…"
            value={search} onChangeText={setSearch} placeholderTextColor="#C7C7CC"/>
          {search?<TouchableOpacity onPress={()=>setSearch('')}><Ionicons name="close-circle" size={18} color="#C7C7CC"/></TouchableOpacity>:null}
        </View>
        <TouchableOpacity style={[st.filterBtn,filters.type!=='All'&&st.filterBtnOn]} onPress={()=>setShowFilters(true)}>
          <Ionicons name="options" size={20} color="#667eea"/>
        </TouchableOpacity>
      </View>

      {/* Sort chips */}
      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={st.sortRow}>
        {[{k:'match',l:'\uD83C\uDFAF Best Match'},{k:'date',l:'\uD83D\uDD52 Latest'},{k:'stipend',l:'\uD83D\uDCB0 Top Pay'},{k:'rating',l:'\u2B50 Top Rated'}].map(o=>(
          <TouchableOpacity key={o.k} style={[st.sortChip,sortBy===o.k&&st.sortChipOn]} onPress={()=>setSortBy(o.k)}>
            <Text style={[st.sortChipT,sortBy===o.k&&st.sortChipTOn]}>{o.l}</Text>
          </TouchableOpacity>
        ))}
        <TouchableOpacity style={st.sortChip} onPress={()=>setViewMode(p=>p==='card'?'compact':'card')}>
          <Ionicons name={viewMode==='card'?'list-outline':'grid-outline'} size={14} color="#667eea"/>
        </TouchableOpacity>
      </ScrollView>

      {/* Company spotlight */}
      <View style={{marginBottom:4}}>
        <Text style={st.sectionHdr}>\uD83C\uDFE2 Featured Companies</Text>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{paddingHorizontal:16,gap:10}}>
          {COMPANIES.map(co=>(
            <View key={co.name} style={st.coCard}>
              <Image source={{uri:co.logo}} style={st.coLogo}/>
              <Text style={st.coName} numberOfLines={1}>{co.name}</Text>
              <View style={{flexDirection:'row',alignItems:'center',gap:2,marginTop:2}}>
                <MaterialCommunityIcons name="star" size={11} color="#F59E0B"/>
                <Text style={{fontSize:11,color:'#F59E0B',fontWeight:'600'}}>{co.rating}</Text>
              </View>
              <Text style={st.coOpenings}>{co.openings} open</Text>
            </View>
          ))}
        </ScrollView>
      </View>

      {/* Compare bar */}
      {compareList.length>0&&(
        <TouchableOpacity style={st.cmpBar} onPress={()=>setShowCompare(true)}>
          <Ionicons name="git-compare-outline" size={15} color="#FFF"/>
          <Text style={st.cmpBarT}>Compare {compareList.length} internship{compareList.length>1?'s':''}</Text>
          <TouchableOpacity onPress={()=>setCompareList([])} hitSlop={{top:8,bottom:8,left:8,right:8}}>
            <Ionicons name="close" size={15} color="#FFF"/>
          </TouchableOpacity>
        </TouchableOpacity>
      )}

      <Text style={st.resultsCount}>{filteredPrograms.length} internship{filteredPrograms.length!==1?'s':''} found</Text>

      <FlatList data={filteredPrograms} keyExtractor={i=>i.id}
        renderItem={({item})=>viewMode==='compact'?renderCompactCard(item):renderProgramCard(item)}
        contentContainerStyle={{paddingBottom:40}} showsVerticalScrollIndicator={false}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor="#667eea"/>}
        ListEmptyComponent={()=>(
          <View style={st.empty}>
            <MaterialCommunityIcons name="briefcase-search" size={64} color="#C7C7CC"/>
            <Text style={st.emptyTitle}>No Internships Found</Text>
            <Text style={st.emptyText}>Try adjusting your search or filters</Text>
            <TouchableOpacity style={st.emptyBtn} onPress={()=>{setSearch('');setFilters({type:'All',department:'All',location:'All',remote:false,matchMin:0});}}>
              <Text style={st.emptyBtnT}>Clear Filters</Text>
            </TouchableOpacity>
          </View>
        )}
      />
    </Animated.View>
  );

  const renderProgramCard = (item) => {
    const isSaved=savedIds.has(item.id), isApplied=appliedIds.has(item.id);
    const inCmp=compareList.some(p=>p.id===item.id), days=daysLeft(item.applicationDeadline);
    return (
      <TouchableOpacity key={item.id} style={st.progCard} activeOpacity={0.95}
        onPress={()=>{setSelectedItem(item);setModalType('detail');setShowModal(true);}}>
        {item.matchScore>=80&&<View style={st.matchPill}><Text style={st.matchPillT}>{item.matchScore}% Match</Text></View>}
        {item.published&&item.companyName&&<View style={{position:'absolute',top:12,left:12,backgroundColor:'#10B981',borderRadius:6,paddingHorizontal:6,paddingVertical:2}}><Text style={{fontSize:9,fontWeight:'700',color:'#FFF'}}>✓ VERIFIED</Text></View>}
        <View style={st.cardTop}>
          <Image source={{uri:item.logo}} style={st.logo}/>
          <View style={{flex:1}}>
            <Text style={st.progTitle}>{item.title}</Text>
            <Text style={st.progCo}>{item.company}</Text>
            <View style={st.metaRow}>
              <View style={st.metaItem}><Ionicons name="location-outline" size={12} color="#8E8E93"/><Text style={st.metaT}>{item.location}</Text></View>
              <View style={st.metaItem}><Ionicons name="time-outline" size={12} color="#8E8E93"/><Text style={st.metaT}>{item.duration}</Text></View>
              <View style={st.metaItem}><Ionicons name="cash-outline" size={12} color="#10B981"/><Text style={[st.metaT,{color:'#10B981',fontWeight:'600'}]}>{item.stipend}</Text></View>
            </View>
          </View>
          <View style={{gap:5}}>
            <TouchableOpacity style={st.iconBtn} onPress={()=>toggleSave(item.id)}>
              <Ionicons name={isSaved?'bookmark':'bookmark-outline'} size={18} color={isSaved?'#667eea':'#C7C7CC'}/>
            </TouchableOpacity>
            <TouchableOpacity style={st.iconBtn} onPress={()=>shareProgram(item)}>
              <Ionicons name="share-social-outline" size={18} color="#C7C7CC"/>
            </TouchableOpacity>
          </View>
        </View>
        <Text style={st.progDesc} numberOfLines={2}>{item.description}</Text>
        <View style={st.skillsRow}>
          {(item.skills||[]).slice(0,4).map((sk,i)=><View key={i} style={st.skillChip}><Text style={st.skillChipT}>{sk}</Text></View>)}
          {(item.skills||[]).length>4&&<Text style={st.more}>+{(item.skills||[]).length-4}</Text>}
        </View>
        <View style={st.cardFoot}>
          <View style={{flexDirection:'row',alignItems:'center',gap:4}}>
            <MaterialCommunityIcons name="star" size={13} color="#F59E0B"/>
            <Text style={st.statT}>{item.rating}</Text>
            <MaterialCommunityIcons name="account-group-outline" size={13} color="#8E8E93" style={{marginLeft:6}}/>
            <Text style={st.statT}>{item.applicants}</Text>
            <View style={[st.typePill,item.type==='Virtual'?st.tpGreen:item.type==='Hybrid'?st.tpBlue:st.tpOrange]}>
              <Text style={st.typePillT}>{item.type}</Text>
            </View>
          </View>
          <View style={{flexDirection:'row',gap:6}}>
            <TouchableOpacity style={[st.cmpBtn,inCmp&&st.cmpBtnOn]} onPress={()=>toggleCompare(item)}>
              <Ionicons name="git-compare-outline" size={13} color={inCmp?'#FFF':'#667eea'}/>
            </TouchableOpacity>
            {isApplied
              ?<View style={st.appliedPill}><Text style={st.appliedPillT}>Applied \u2713</Text></View>
              :<TouchableOpacity style={[st.applyBtn,days<3&&{backgroundColor:'#EF4444'}]} onPress={()=>openApply(item)}>
                <Text style={st.applyBtnT}>{days<3?`${days}d left!`:'Apply'}</Text>
              </TouchableOpacity>
            }
          </View>
        </View>
      </TouchableOpacity>
    );
  };

  const renderCompactCard = (item) => {
    const isSaved=savedIds.has(item.id), isApplied=appliedIds.has(item.id);
    return (
      <TouchableOpacity key={item.id} style={st.compactCard} activeOpacity={0.9}
        onPress={()=>{setSelectedItem(item);setModalType('detail');setShowModal(true);}}>
        <Image source={{uri:item.logo}} style={st.compactLogo}/>
        <View style={{flex:1}}>
          <Text style={st.compactTitle} numberOfLines={1}>{item.title}</Text>
          <Text style={st.compactCo}>{item.company} · {item.location}</Text>
          <Text style={{fontSize:11,color:'#10B981',fontWeight:'600',marginTop:1}}>{item.stipend} · {item.duration}</Text>
        </View>
        <TouchableOpacity style={st.iconBtn} onPress={()=>toggleSave(item.id)}>
          <Ionicons name={isSaved?'bookmark':'bookmark-outline'} size={16} color={isSaved?'#667eea':'#C7C7CC'}/>
        </TouchableOpacity>
        {isApplied
          ?<View style={[st.applyBtn,{backgroundColor:'#E5E7EB',paddingHorizontal:8}]}><Text style={[st.applyBtnT,{color:'#6B7280'}]}>Applied</Text></View>
          :<TouchableOpacity style={[st.applyBtn,{paddingHorizontal:10}]} onPress={()=>openApply(item)}><Text style={st.applyBtnT}>Apply</Text></TouchableOpacity>
        }
      </TouchableOpacity>
    );
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // RENDER: Applications tab
  // ─────────────────────────────────────────────────────────────────────────────
  const renderApplications = () => {
    const apps = studentData.applications||[];
    return (
      <Animated.View style={[st.content,{opacity:fadeAnim}]}>
        <View style={st.appSummary}>
          {[['Total',apps.length,'#667eea'],['Active',apps.filter(a=>!['Rejected','Withdrawn'].includes(a.status)).length,'#3B82F6'],
            ['Interview',apps.filter(a=>a.status==='Interview Scheduled').length,'#F59E0B'],
            ['Accepted',apps.filter(a=>a.status==='Accepted').length,'#10B981']].map(([k,v,c])=>(
            <View key={k} style={st.appStatCard}>
              <Text style={[st.appStatNum,{color:c}]}>{v}</Text>
              <Text style={st.appStatLbl}>{k}</Text>
            </View>
          ))}
        </View>
        <FlatList data={apps} keyExtractor={i=>i.id}
          contentContainerStyle={{paddingHorizontal:16,paddingBottom:40}} showsVerticalScrollIndicator={false}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh}/>}
          renderItem={({item:app})=>(
            <View style={[st.appCard,{borderLeftColor:sc(app.status)}]}>
              <View style={st.appTop}>
                <View style={{flex:1}}>
                  <Text style={st.appTitle}>{app.programTitle}</Text>
                  <Text style={st.appCo}>{app.company}</Text>
                  <Text style={st.appDate}>Applied {fmt(app.appliedDate)}</Text>
                </View>
                <View style={[st.statusBadge,{backgroundColor:sc(app.status)+'22'}]}>
                  <Text style={[st.statusT,{color:sc(app.status)}]}>{app.status}</Text>
                </View>
              </View>
              <View style={{marginBottom:10}}>
                <View style={{flexDirection:'row',justifyContent:'space-between',marginBottom:5}}>
                  <Text style={st.progressLabel}>Progress</Text>
                  <Text style={{fontSize:12,fontWeight:'700',color:'#374151'}}>{app.progress}%</Text>
                </View>
                <View style={st.progBar}><View style={[st.progFill,{width:`${app.progress}%`,backgroundColor:sc(app.status)}]}/></View>
              </View>
              <View style={{flexDirection:'row',alignItems:'center',gap:6,marginBottom:6}}>
                <Ionicons name="arrow-forward-circle-outline" size={15} color="#667eea"/>
                <Text style={{fontSize:13,color:'#374151'}}>Next: {app.nextStep}</Text>
              </View>
              {app.interviewDate&&(
                <View style={{flexDirection:'row',alignItems:'center',gap:6,backgroundColor:'#DBEAFE',borderRadius:8,padding:8,marginBottom:6}}>
                  <MaterialCommunityIcons name="calendar-clock" size={14} color="#1D4ED8"/>
                  <Text style={{fontSize:12,color:'#1D4ED8',fontWeight:'600'}}>Interview: {fmt(app.interviewDate)} ({app.interviewType})</Text>
                </View>
              )}
              {app.feedback&&(
                <View style={{flexDirection:'row',gap:6,backgroundColor:'#F3F4F6',borderRadius:8,padding:8,marginBottom:8}}>
                  <Ionicons name="chatbubble-ellipses-outline" size={13} color="#667eea"/>
                  <Text style={{flex:1,fontSize:12,color:'#6B7280',lineHeight:18}} numberOfLines={2}>{app.feedback}</Text>
                </View>
              )}
              <View style={{flexDirection:'row',gap:8,borderTopWidth:1,borderTopColor:'#F3F4F6',paddingTop:10}}>
                <TouchableOpacity style={st.appActionBtn} onPress={()=>{setSelectedItem(app);setModalType('appDetail');setShowModal(true);}}>
                  <Text style={st.appActionBtnT}>View Details</Text>
                  <Ionicons name="chevron-forward" size={14} color="#667eea"/>
                </TouchableOpacity>
                {!['Rejected','Withdrawn','Accepted'].includes(app.status)&&(
                  <TouchableOpacity style={[st.appActionBtn,{backgroundColor:'#FEF2F2'}]} onPress={()=>withdrawApp(app)}>
                    <Text style={[st.appActionBtnT,{color:'#EF4444'}]}>Withdraw</Text>
                  </TouchableOpacity>
                )}
              </View>
            </View>
          )}
          ListEmptyComponent={()=>(
            <View style={st.empty}>
              <MaterialCommunityIcons name="file-document-outline" size={64} color="#C7C7CC"/>
              <Text style={st.emptyTitle}>No Applications Yet</Text>
              <Text style={st.emptyText}>Start applying to internships to track them here</Text>
              <TouchableOpacity style={st.emptyBtn} onPress={()=>setActiveTab('discover')}><Text style={st.emptyBtnT}>Browse Internships</Text></TouchableOpacity>
            </View>
          )}
        />
      </Animated.View>
    );
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // RENDER: Saved tab
  // ─────────────────────────────────────────────────────────────────────────────
  const renderSaved = () => {
    const saved = PROGRAMS.filter(p=>savedIds.has(p.id));
    return (
      <Animated.View style={[st.content,{opacity:fadeAnim}]}>
        {saved.length>0&&(
          <View style={{flexDirection:'row',justifyContent:'space-between',alignItems:'center',padding:16}}>
            <Text style={{fontSize:14,fontWeight:'600',color:'#374151'}}>{saved.length} saved</Text>
            <TouchableOpacity onPress={()=>{const up={...studentData,savedPrograms:[]};setStudentData(up);persistData(up);}}>
              <Text style={{fontSize:13,color:'#EF4444',fontWeight:'500'}}>Clear All</Text>
            </TouchableOpacity>
          </View>
        )}
        <FlatList data={saved} keyExtractor={i=>i.id} renderItem={({item})=>renderProgramCard(item)}
          contentContainerStyle={{paddingBottom:40}} showsVerticalScrollIndicator={false}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh}/>}
          ListEmptyComponent={()=>(
            <View style={st.empty}>
              <MaterialCommunityIcons name="bookmark-outline" size={64} color="#C7C7CC"/>
              <Text style={st.emptyTitle}>No Saved Internships</Text>
              <Text style={st.emptyText}>Bookmark internships to review them later</Text>
              <TouchableOpacity style={st.emptyBtn} onPress={()=>setActiveTab('discover')}><Text style={st.emptyBtnT}>Browse</Text></TouchableOpacity>
            </View>
          )}
        />
      </Animated.View>
    );
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // RENDER: Profile tab
  // ─────────────────────────────────────────────────────────────────────────────
  const renderProfile = () => {
    const p=studentData.profile;
    const apps=studentData.applications||[];
    const accepted=apps.filter(a=>a.status==='Accepted').length;
    return (
      <ScrollView style={st.content} contentContainerStyle={{paddingBottom:40}} showsVerticalScrollIndicator={false}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh}/>}>
        <LinearGradient colors={['#667eea','#764ba2']} style={{padding:24,alignItems:'center'}}>
          <TouchableOpacity onPress={pickProfileImage} style={{ marginBottom:10, position:'relative' }}>
            {isImgUri(editedAvatar || p.avatar) ? (
              <Image
                source={{ uri: editedAvatar || p.avatar }}
                style={st.avatar}
                onError={() => {}}
              />
            ) : (
              <View style={[st.avatar,{backgroundColor:'rgba(255,255,255,0.2)',justifyContent:'center',alignItems:'center'}]}>
                <Text style={{fontSize:28,fontWeight:'800',color:'#FFF'}}>{(p.name||'U').split(' ').map(w=>w?.[0]||'').join('').slice(0,2).toUpperCase()}</Text>
              </View>
            )}
            <View style={{ position:'absolute', bottom:2, right:2, width:26, height:26, borderRadius:13, backgroundColor:'#667eea', justifyContent:'center', alignItems:'center', borderWidth:2, borderColor:'rgba(255,255,255,0.9)' }}>
              <Ionicons name="camera" size={13} color="#FFF"/>
            </View>
          </TouchableOpacity>
          <Text style={st.profName}>{p.name}</Text>
          <Text style={{fontSize:13,color:'rgba(255,255,255,0.85)',marginTop:2}}>{p.major} · {p.year}</Text>
          <Text style={{fontSize:12,color:'rgba(255,255,255,0.7)',marginTop:1}}>{p.university}</Text>
          <View style={st.profStats}>
            {[['Applied',apps.length],['Saved',savedIds.size],['Accepted',accepted],['GPA',p.gpa]].map(([k,v],i,arr)=>(
              <React.Fragment key={k}>
                <View style={{alignItems:'center'}}>
                  <Text style={{fontSize:20,fontWeight:'900',color:'#FFF'}}>{v}</Text>
                  <Text style={{fontSize:10,color:'rgba(255,255,255,0.7)',marginTop:1}}>{k}</Text>
                </View>
                {i<arr.length-1&&<View style={{width:1,backgroundColor:'rgba(255,255,255,0.3)'}}/>}
              </React.Fragment>
            ))}
          </View>
          <TouchableOpacity style={st.editProfBtn} onPress={()=>{setEditProfile({...p});setModalType('editProfile');setShowModal(true);}}>
            <Ionicons name="pencil" size={15} color="#667eea"/>
            <Text style={{fontSize:13,fontWeight:'700',color:'#667eea'}}>Edit Profile</Text>
          </TouchableOpacity>
        </LinearGradient>

        {/* Completeness */}
        <View style={st.profSection}>
          <View style={{flexDirection:'row',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
            <Text style={st.secTitle}>Profile Completeness</Text>
            <Text style={{fontSize:14,fontWeight:'800',color:p.completeness>=80?'#10B981':p.completeness>=50?'#F59E0B':'#EF4444'}}>{p.completeness}%</Text>
          </View>
          <View style={{height:10,backgroundColor:'#E5E7EB',borderRadius:5,overflow:'hidden'}}>
            <View style={{height:10,borderRadius:5,backgroundColor:p.completeness>=80?'#10B981':p.completeness>=50?'#F59E0B':'#EF4444',width:`${p.completeness}%`}}/>
          </View>
          {p.completeness<80&&<Text style={{fontSize:12,color:'#8E8E93',marginTop:5}}>{p.completeness<50?'Complete your profile to improve visibility':'Almost there — add more details to stand out'}</Text>}
        </View>

        {p.bio&&<View style={st.profSection}><Text style={st.secTitle}>About</Text><Text style={{fontSize:14,color:'#6B7280',lineHeight:20}}>{p.bio}</Text></View>}

        <View style={st.profSection}>
          <Text style={st.secTitle}>Academic Info</Text>
          <View style={{flexDirection:'row',flexWrap:'wrap',gap:8}}>
            {[['GPA',p.gpa],['Year',p.year],['Major',p.major],['Available',p.availability||'TBD']].map(([k,v])=>(
              <View key={k} style={{flex:1,minWidth:120,backgroundColor:'#F8F9FA',borderRadius:10,padding:10}}>
                <Text style={{fontSize:11,color:'#8E8E93',fontWeight:'600',marginBottom:2}}>{k}</Text>
                <Text style={{fontSize:14,fontWeight:'700',color:'#1C1C1E'}}>{v}</Text>
              </View>
            ))}
          </View>
        </View>

        <View style={st.profSection}>
          <View style={{flexDirection:'row',justifyContent:'space-between',marginBottom:8}}>
            <Text style={st.secTitle}>Skills</Text>
            <TouchableOpacity onPress={()=>{setEditProfile({...p});setModalType('editProfile');setShowModal(true);}}>
              <Text style={{fontSize:13,color:'#667eea',fontWeight:'600'}}>Edit</Text>
            </TouchableOpacity>
          </View>
          <View style={{flexDirection:'row',flexWrap:'wrap',gap:8}}>
            {(p.skills||[]).map((sk,i)=><View key={i} style={{backgroundColor:'#EEF2FF',borderRadius:8,paddingHorizontal:10,paddingVertical:5}}><Text style={{fontSize:12,color:'#667eea',fontWeight:'600'}}>{sk}</Text></View>)}
          </View>
        </View>

        <View style={st.profSection}>
          <Text style={st.secTitle}>Interests</Text>
          <View style={{flexDirection:'row',flexWrap:'wrap',gap:8}}>
            {(p.interests||[]).map((int,i)=><View key={i} style={{backgroundColor:'#FEF3C7',borderRadius:8,paddingHorizontal:10,paddingVertical:5}}><Text style={{fontSize:12,color:'#D97706',fontWeight:'600'}}>{int}</Text></View>)}
          </View>
        </View>

        <View style={st.profSection}>
          <Text style={st.secTitle}>Links</Text>
          {[{icon:'document-text',label:'Resume',val:p.resume},{icon:'globe-outline',label:'Portfolio',val:p.portfolio},{icon:'logo-linkedin',label:'LinkedIn',val:p.linkedin},{icon:'logo-github',label:'GitHub',val:p.github}].map(lk=>(
            <TouchableOpacity key={lk.label} style={{flexDirection:'row',alignItems:'center',paddingVertical:11,borderBottomWidth:1,borderBottomColor:'#F3F4F6',gap:10}}
              onPress={()=>lk.val&&lk.val.startsWith('http')&&Linking.openURL(lk.val).catch(()=>{})}>
              <Ionicons name={lk.icon} size={19} color="#667eea"/>
              <Text style={{fontSize:14,fontWeight:'600',color:'#374151',width:80}}>{lk.label}</Text>
              <Text style={{flex:1,fontSize:13,color:'#8E8E93'}} numberOfLines={1}>{lk.val||'Not added'}</Text>
              <Ionicons name="open-outline" size={14} color="#C7C7CC"/>
            </TouchableOpacity>
          ))}
        </View>

        <View style={st.profSection}>
          <Text style={st.secTitle}>Achievements</Text>
          <View style={{flexDirection:'row',gap:10}}>
            {(studentData.achievements||[]).map(ach=>(
              <View key={ach.id} style={{flex:1,backgroundColor:ach.earned?ach.color+'15':'#F3F4F6',borderRadius:12,padding:10,alignItems:'center',borderWidth:1,borderColor:ach.earned?ach.color+'33':'#E5E7EB',opacity:ach.earned?1:0.5}}>
                <Ionicons name={ach.icon} size={22} color={ach.earned?ach.color:'#C7C7CC'}/>
                <Text style={{fontSize:10,fontWeight:'600',color:ach.earned?'#374151':'#C7C7CC',marginTop:5,textAlign:'center'}}>{ach.title}</Text>
              </View>
            ))}
          </View>
        </View>
      </ScrollView>
    );
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // RENDER: Analytics tab
  // ─────────────────────────────────────────────────────────────────────────────
  const renderAnalytics = () => {
    const apps=studentData.applications||[];
    const total=apps.length, accepted=apps.filter(a=>a.status==='Accepted').length;
    const interviews=apps.filter(a=>a.status==='Interview Scheduled').length;
    const avgProg=total?Math.round(apps.reduce((s,a)=>s+(a.progress||0),0)/total):0;
    const successRate=total?Math.round(accepted/total*100):0;
    return (
      <ScrollView style={st.content} contentContainerStyle={{padding:16,paddingBottom:40}} showsVerticalScrollIndicator={false}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh}/>}>
        <Text style={[st.secTitle,{marginBottom:12}]}>\uD83D\uDCCA Application Dashboard</Text>
        <View style={{flexDirection:'row',flexWrap:'wrap',gap:10,marginBottom:14}}>
          {[{l:'Applications',v:total,c:['#667eea','#764ba2'],i:'send-outline'},
            {l:'Interviews',v:interviews,c:['#3B82F6','#60A5FA'],i:'calendar-outline'},
            {l:'Accepted',v:accepted,c:['#10B981','#34D399'],i:'checkmark-circle-outline'},
            {l:'Success Rate',v:`${successRate}%`,c:['#F59E0B','#FBBF24'],i:'trending-up-outline'}].map(m=>(
            <LinearGradient key={m.l} colors={m.c} start={{x:0,y:0}} end={{x:1,y:1}} style={{width:(width-52)/2,borderRadius:14,padding:14,gap:4}}>
              <Ionicons name={m.i} size={21} color="#FFF"/>
              <Text style={{fontSize:24,fontWeight:'900',color:'#FFF',marginTop:3}}>{m.v}</Text>
              <Text style={{fontSize:11,color:'rgba(255,255,255,0.85)',fontWeight:'600'}}>{m.l}</Text>
            </LinearGradient>
          ))}
        </View>

        <View style={st.aCard}>
          <Text style={st.aCardTitle}>Status Breakdown</Text>
          {[{l:'Submitted',c:'#8B5CF6'},{l:'Under Review',c:'#F59E0B'},{l:'Interview Scheduled',c:'#3B82F6'},{l:'Accepted',c:'#10B981'},{l:'Rejected',c:'#EF4444'}].map(row=>(
            <View key={row.l} style={{flexDirection:'row',alignItems:'center',marginBottom:8,gap:8}}>
              <View style={{width:9,height:9,borderRadius:5,backgroundColor:row.c}}/>
              <Text style={{width:120,fontSize:12,color:'#6B7280'}}>{row.l}</Text>
              <View style={{flex:1,height:7,backgroundColor:'#F3F4F6',borderRadius:4,overflow:'hidden'}}>
                <View style={{height:7,borderRadius:4,backgroundColor:row.c,width:`${total?Math.round(apps.filter(a=>a.status===row.l).length/total*100):0}%`}}/>
              </View>
              <Text style={{fontSize:12,fontWeight:'700',color:'#374151',width:18,textAlign:'right'}}>{apps.filter(a=>a.status===row.l).length}</Text>
            </View>
          ))}
        </View>

        {total>0&&(
          <View style={st.aCard}>
            <Text style={st.aCardTitle}>Average Progress</Text>
            <View style={{flexDirection:'row',alignItems:'center',gap:12}}>
              <Text style={{fontSize:28,fontWeight:'900',color:'#667eea',width:56}}>{avgProg}%</Text>
              <View style={{flex:1,height:10,backgroundColor:'#F3F4F6',borderRadius:5,overflow:'hidden'}}>
                <View style={{height:10,borderRadius:5,backgroundColor:'#667eea',width:`${avgProg}%`}}/>
              </View>
            </View>
          </View>
        )}

        <LinearGradient colors={['#667eea','#764ba2']} style={[st.aCard,{borderWidth:0}]}>
          <Text style={[st.aCardTitle,{color:'#FFF'}]}>\uD83E\uDD16 AI Insights</Text>
          {[
            total===0?'\uD83D\uDE80 Start applying! Students who apply to 5+ internships are 3\u00D7 more likely to get an offer.':
              accepted>0?`\uD83C\uDF89 You have ${accepted} acceptance${accepted>1?'s':''}! Reply promptly to confirm your spot.`:
              interviews>0?`\uD83D\uDCBC ${interviews} interview${interviews>1?'s':''} scheduled. Prepare with mock interviews and company research.`:
              `\uD83D\uDCE8 ${total} application${total>1?'s':''} submitted. Expect responses within 1\u20132 weeks.`,
            savedIds.size>=5?`\uD83D\uDD16 You've saved ${savedIds.size} internships. Time to apply to your top picks!`:'Save internships you like to quickly revisit and compare later.',
            studentData.profile.completeness<80?`\u26A0\uFE0F Complete your profile (${studentData.profile.completeness}%) to be prioritised by recruiters.`:`\u2705 Strong profile score (${studentData.profile.completeness}%)! You're visible to top companies.`,
          ].map((ins,i)=>(
            <View key={i} style={{backgroundColor:'rgba(255,255,255,0.15)',borderRadius:10,padding:10,marginBottom:8}}>
              <Text style={{fontSize:13,color:'#FFF',lineHeight:18}}>{ins}</Text>
            </View>
          ))}
        </LinearGradient>

        <View style={st.aCard}>
          <Text style={st.aCardTitle}>Your Top Matches</Text>
          {PROGRAMS.sort((a,b)=>(b.matchScore||0)-(a.matchScore||0)).slice(0,3).map(prog=>(
            <TouchableOpacity key={prog.id} style={{flexDirection:'row',alignItems:'center',paddingVertical:8,borderBottomWidth:1,borderBottomColor:'#F3F4F6',gap:10}}
              onPress={()=>{setSelectedItem(prog);setModalType('detail');setShowModal(true);setActiveTab('discover');}}>
              <Image source={{uri:prog.logo}} style={{width:34,height:34,borderRadius:8}}/>
              <View style={{flex:1}}>
                <Text style={{fontSize:14,fontWeight:'600',color:'#1C1C1E'}}>{prog.title}</Text>
                <Text style={{fontSize:12,color:'#8E8E93',marginTop:1}}>{prog.company} · {prog.stipend}</Text>
              </View>
              <View style={{backgroundColor:'#EEF2FF',borderRadius:8,paddingHorizontal:8,paddingVertical:3}}>
                <Text style={{fontSize:12,fontWeight:'700',color:'#667eea'}}>{prog.matchScore}%</Text>
              </View>
            </TouchableOpacity>
          ))}
        </View>
      </ScrollView>
    );
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // RENDER: Modals
  // ─────────────────────────────────────────────────────────────────────────────
  const ModalShell = ({title,onClose,rightEl,children}) => (
    <View style={{flex:1,backgroundColor:'#FFF'}}>
      <View style={st.mHdr}>
        <TouchableOpacity style={st.mCloseBtn} onPress={onClose}><Ionicons name="close" size={22} color="#8E8E93"/></TouchableOpacity>
        <Text style={st.mTitle} numberOfLines={1}>{title}</Text>
        {rightEl||<View style={{width:36}}/>}
      </View>
      {children}
    </View>
  );

  const SaveBtn = ({label,onPress,disabled}) => (
    <TouchableOpacity style={[st.mSaveBtn,disabled&&{opacity:0.6}]} onPress={onPress} disabled={disabled}>
      <Text style={st.mSaveBtnT}>{label}</Text>
    </TouchableOpacity>
  );

  const renderDetailModal = () => {
    const prog=selectedItem; if(!prog) return null;
    const isApplied=appliedIds.has(prog.id), isSaved=savedIds.has(prog.id), days=daysLeft(prog.applicationDeadline);
    return (
      <ModalShell title={prog.title} onClose={()=>setShowModal(false)}
        rightEl={<TouchableOpacity style={st.mCloseBtn} onPress={()=>toggleSave(prog.id)}><Ionicons name={isSaved?'bookmark':'bookmark-outline'} size={22} color={isSaved?'#667eea':'#8E8E93'}/></TouchableOpacity>}>
        <ScrollView showsVerticalScrollIndicator={false}>
          <LinearGradient colors={['#667eea','#764ba2']} style={{padding:20,alignItems:'center'}}>
            <Image source={{uri:prog.logo}} style={{width:56,height:56,borderRadius:14,marginBottom:10}}/>
            <Text style={{fontSize:18,fontWeight:'800',color:'#FFF',textAlign:'center'}}>{prog.title}</Text>
            <Text style={{fontSize:14,color:'rgba(255,255,255,0.85)',marginTop:3}}>{prog.company}</Text>
            <View style={{flexDirection:'row',flexWrap:'wrap',justifyContent:'center',gap:12,marginTop:10}}>
              {[[`location-outline`,prog.location],[`time-outline`,prog.duration],[`cash-outline`,prog.stipend]].map(([icon,val],i)=>(
                <View key={i} style={{flexDirection:'row',alignItems:'center',gap:4}}>
                  <Ionicons name={icon} size={13} color={i===2?'#6EE7B7':'rgba(255,255,255,0.75)'}/>
                  <Text style={{fontSize:12,color:i===2?'#6EE7B7':'rgba(255,255,255,0.8)',fontWeight:i===2?'700':'400'}}>{val}</Text>
                </View>
              ))}
            </View>
          </LinearGradient>

          <View style={{flexDirection:'row',justifyContent:'space-around',backgroundColor:'#FFF',margin:16,borderRadius:14,padding:12,elevation:1,shadowColor:'#000',shadowOpacity:0.05,shadowRadius:4}}>
            {[[prog.rating,'Rating','#F59E0B'],[prog.applicants,'Applicants','#8E8E93'],[`${days}d`,'Left',days<3?'#EF4444':'#8E8E93'],[`${prog.matchScore}%`,'Match','#667eea']].map(([v,l,c])=>(
              <View key={l} style={{alignItems:'center',gap:2}}>
                <Text style={{fontSize:18,fontWeight:'800',color:c}}>{v}</Text>
                <Text style={{fontSize:10,color:'#8E8E93'}}>{l}</Text>
              </View>
            ))}
          </View>

          {[{t:'About the Role',body:prog.description,type:'text'},
            {t:'Requirements',body:prog.requirements,type:'list',color:'#667eea'},
            {t:'Responsibilities',body:prog.responsibilities,type:'list',color:'#10B981'},
            {t:'Skills Required',body:null,type:'skills'},
            {t:'Benefits & Perks',body:prog.benefits,type:'check'},
          ].map((sec,i)=>(
            <View key={i} style={{paddingHorizontal:16,paddingVertical:12,borderBottomWidth:1,borderBottomColor:'#F3F4F6'}}>
              <Text style={{fontSize:15,fontWeight:'700',color:'#1C1C1E',marginBottom:8}}>{sec.t}</Text>
              {sec.type==='text'&&<Text style={{fontSize:14,color:'#6B7280',lineHeight:20}}>{sec.body}</Text>}
              {sec.type==='skills'&&<View style={{flexDirection:'row',flexWrap:'wrap',gap:8}}>{(prog.skills||[]).map((sk,j)=><View key={j} style={{backgroundColor:'#EEF2FF',borderRadius:8,paddingHorizontal:8,paddingVertical:4}}><Text style={{fontSize:12,color:'#667eea',fontWeight:'600'}}>{sk}</Text></View>)}</View>}
              {(sec.type==='list'||sec.type==='check')&&(sec.body||[]).map((item,j)=>(
                <View key={j} style={{flexDirection:'row',alignItems:'flex-start',gap:8,marginBottom:5}}>
                  {sec.type==='check'?<Ionicons name="checkmark-circle" size={14} color="#10B981" style={{marginTop:2}}/>:<View style={{width:7,height:7,borderRadius:4,backgroundColor:sec.color,marginTop:5}}/>}
                  <Text style={{flex:1,fontSize:13,color:'#374151',lineHeight:18}}>{item}</Text>
                </View>
              ))}
            </View>
          ))}

          <View style={{paddingHorizontal:16,paddingVertical:12}}>
            <Text style={{fontSize:15,fontWeight:'700',color:'#1C1C1E',marginBottom:10}}>Company Details</Text>
            {[['Industry',prog.industry],['Size',`${prog.companySize} employees`],['Type',prog.type],['Start Date',fmt(prog.startDate)],['Deadline',fmt(prog.applicationDeadline)]].map(([k,v],i)=>(
              <View key={k} style={{flexDirection:'row',justifyContent:'space-between',paddingVertical:7,borderBottomWidth:1,borderBottomColor:'#F8F9FA'}}>
                <Text style={{fontSize:13,color:'#8E8E93'}}>{k}</Text>
                <Text style={{fontSize:13,color:'#374151',fontWeight:'600'}}>{v}</Text>
              </View>
            ))}
          </View>
          <View style={{height:100}}/>
        </ScrollView>

        <View style={{flexDirection:'row',gap:10,padding:16,borderTopWidth:1,borderTopColor:'#E5E5EA'}}>
          <TouchableOpacity style={st.shareBtn} onPress={()=>shareProgram(prog)}><Ionicons name="share-social-outline" size={18} color="#667eea"/></TouchableOpacity>
          {isApplied
            ?<View style={[st.applyModalBtn,{backgroundColor:'#E5E7EB',flex:1}]}><Text style={[st.applyModalBtnT,{color:'#6B7280'}]}>Applied \u2713</Text></View>
            :<TouchableOpacity style={[st.applyModalBtn,days<3&&{backgroundColor:'#EF4444'}]} onPress={()=>{setShowModal(false);setTimeout(()=>openApply(prog),300);}}>
              <Text style={st.applyModalBtnT}>{days<3?`Apply Now · ${days}d Left!`:'Apply Now'}</Text>
            </TouchableOpacity>
          }
        </View>
      </ModalShell>
    );
  };

  const renderApplyModal = () => {
    const prog=selectedItem; if(!prog) return null;
    const fields = [
      {label:'Cover Letter *',key:'coverLetter',multi:true,placeholder:'Write a compelling cover letter…'},
      {label:'Why interested? *',key:'whyInterested',multi:true,placeholder:'Explain your motivation for this specific role…'},
      {label:'Relevant Experience',key:'relevantExperience',multi:true,placeholder:'Projects, internships, coursework…'},
      {label:'Availability *',key:'availability',multi:false,placeholder:'e.g. Available from December 2024, 12 weeks'},
      {label:'Additional Info',key:'additionalInfo',multi:true,placeholder:"Anything else you'd like us to know?"},
    ];
    return (
      <KeyboardAvoidingView style={{flex:1}} behavior={Platform.OS==='ios'?'padding':'height'}>
        <ModalShell title={`Apply — ${prog.company}`} onClose={()=>setShowModal(false)}
          rightEl={<SaveBtn label={savingForm?'Sending…':'Submit'} onPress={submitApp} disabled={savingForm}/>}>
          <ScrollView style={{flex:1,padding:16}} showsVerticalScrollIndicator={false} keyboardShouldPersistTaps="handled">
            <View style={{flexDirection:'row',alignItems:'center',backgroundColor:'#EEF2FF',borderRadius:12,padding:12,marginBottom:16,gap:10}}>
              <Image source={{uri:prog.logo}} style={{width:38,height:38,borderRadius:8}}/>
              <View><Text style={{fontSize:14,fontWeight:'700',color:'#1C1C1E'}}>{prog.title}</Text><Text style={{fontSize:12,color:'#667eea',marginTop:1}}>{prog.company} · {prog.stipend}</Text></View>
            </View>
            {fields.map(f=>(
              <View key={f.key} style={{marginBottom:14}}>
                <Text style={st.formLabel}>{f.label}</Text>
                <TextInput style={[st.formInput,f.multi&&st.textArea]}
                  value={appForm[f.key]} multiline={f.multi} numberOfLines={f.multi?4:1}
                  onChangeText={v=>setAppForm(p=>({...p,[f.key]:v}))}
                  placeholder={f.placeholder} placeholderTextColor="#C7C7CC"/>
              </View>
            ))}
            <View style={{height:20}}/>
          </ScrollView>
        </ModalShell>
      </KeyboardAvoidingView>
    );
  };

  const renderAppDetailModal = () => {
    const app=selectedItem; if(!app) return null;
    return (
      <ModalShell title="Application Details" onClose={()=>setShowModal(false)}>
        <ScrollView style={{flex:1,padding:16}} showsVerticalScrollIndicator={false}>
          <View style={{backgroundColor:sc(app.status)+'18',borderRadius:12,padding:14,marginBottom:14,flexDirection:'row',justifyContent:'space-between',alignItems:'center'}}>
            <Text style={{fontSize:16,fontWeight:'800',color:sc(app.status)}}>{app.status}</Text>
            <Text style={{fontSize:13,color:'#6B7280'}}>{app.progress}% complete</Text>
          </View>
          <Text style={{fontSize:17,fontWeight:'700',color:'#1C1C1E',marginBottom:4}}>{app.programTitle}</Text>
          <Text style={{fontSize:14,color:'#8E8E93',marginBottom:16}}>{app.company}</Text>

          <Text style={{fontSize:14,fontWeight:'700',color:'#1C1C1E',marginBottom:10}}>Timeline</Text>
          {[
            {icon:'send-outline',color:'#667eea',title:'Submitted',date:app.appliedDate},
            app.interviewDate&&{icon:'calendar-outline',color:'#3B82F6',title:`Interview (${app.interviewType})`,date:app.interviewDate},
            app.acceptedDate&&{icon:'checkmark-circle-outline',color:'#10B981',title:'Accepted',date:app.acceptedDate},
            app.startDate&&{icon:'briefcase-outline',color:'#F59E0B',title:'Start Date',date:app.startDate},
          ].filter(Boolean).map((ev,i)=>(
            <View key={i} style={{flexDirection:'row',alignItems:'flex-start',marginBottom:12}}>
              <View style={{width:28,height:28,borderRadius:14,backgroundColor:ev.color,justifyContent:'center',alignItems:'center'}}>
                <Ionicons name={ev.icon} size={12} color="#FFF"/>
              </View>
              <View style={{marginLeft:12,flex:1}}>
                <Text style={{fontSize:14,fontWeight:'600',color:'#374151'}}>{ev.title}</Text>
                <Text style={{fontSize:12,color:'#8E8E93',marginTop:1}}>{fmt(ev.date)}</Text>
              </View>
            </View>
          ))}

          <View style={st.progBar}><View style={[st.progFill,{width:`${app.progress}%`,backgroundColor:sc(app.status)}]}/></View>
          <Text style={{fontSize:13,color:'#374151',marginTop:8,marginBottom:16}}>Next: {app.nextStep}</Text>

          {app.feedback&&(
            <View style={{backgroundColor:'#F3F4F6',borderRadius:10,padding:12,marginBottom:16}}>
              <Text style={{fontSize:13,fontWeight:'600',color:'#374151',marginBottom:4}}>Recruiter Feedback</Text>
              <Text style={{fontSize:13,color:'#6B7280',lineHeight:18}}>{app.feedback}</Text>
            </View>
          )}

          {(app.documents||[]).length>0&&(
            <View>
              <Text style={{fontSize:14,fontWeight:'700',color:'#1C1C1E',marginBottom:8}}>Documents</Text>
              {app.documents.map((doc,i)=>(
                <View key={i} style={{flexDirection:'row',alignItems:'center',gap:10,paddingVertical:8,borderBottomWidth:1,borderBottomColor:'#F3F4F6'}}>
                  <MaterialCommunityIcons name="file-document-outline" size={18} color="#667eea"/>
                  <Text style={{fontSize:14,color:'#374151'}}>{doc}</Text>
                </View>
              ))}
            </View>
          )}

          {!['Rejected','Withdrawn','Accepted'].includes(app.status)&&(
            <TouchableOpacity style={{marginTop:20,backgroundColor:'#FEF2F2',borderRadius:12,paddingVertical:14,alignItems:'center'}} onPress={()=>withdrawApp(app)}>
              <Text style={{fontSize:15,fontWeight:'700',color:'#EF4444'}}>Withdraw Application</Text>
            </TouchableOpacity>
          )}
          <View style={{height:20}}/>
        </ScrollView>
      </ModalShell>
    );
  };

  const renderNotifModal = () => (
    <ModalShell title="Notifications" onClose={()=>setShowModal(false)}
      rightEl={<SaveBtn label="Read All" onPress={markAllRead}/>}>
      <ScrollView style={{flex:1,padding:16}} showsVerticalScrollIndicator={false}>
        {(studentData.notifications||[]).length===0&&(
          <View style={st.empty}><Ionicons name="notifications-off-outline" size={48} color="#C7C7CC"/><Text style={st.emptyTitle}>No notifications</Text></View>
        )}
        {(studentData.notifications||[]).map(n=>{
          const icons={interview:'calendar',acceptance:'checkmark-circle',deadline:'alarm',rejection:'close-circle'};
          const colors={interview:'#3B82F6',acceptance:'#10B981',deadline:'#F59E0B',rejection:'#EF4444'};
          const ic=icons[n.type]||'information', cc=colors[n.type]||'#667eea';
          return (
            <TouchableOpacity key={n.id} onPress={()=>markOneRead(n.id)}
              style={{flexDirection:'row',alignItems:'flex-start',padding:12,borderRadius:12,marginBottom:8,backgroundColor:n.read?'#F8F9FA':'#EEF2FF',gap:10}}>
              <View style={{width:36,height:36,borderRadius:10,backgroundColor:cc+'22',justifyContent:'center',alignItems:'center'}}>
                <MaterialCommunityIcons name={ic} size={19} color={cc}/>
              </View>
              <View style={{flex:1}}>
                <Text style={{fontSize:14,fontWeight:'700',color:'#1C1C1E',marginBottom:3}}>{n.title}</Text>
                <Text style={{fontSize:13,color:'#6B7280',lineHeight:18}}>{n.message}</Text>
                <Text style={{fontSize:11,color:'#C7C7CC',marginTop:3}}>{fmt(n.date)}</Text>
              </View>
              {!n.read&&<View style={{width:10,height:10,borderRadius:5,backgroundColor:'#667eea',marginTop:4}}/>}
            </TouchableOpacity>
          );
        })}
      </ScrollView>
    </ModalShell>
  );

  const renderFiltersModal = () => (
    <View style={{flex:1,backgroundColor:'#FFF'}}>
      <View style={st.mHdr}>
        <TouchableOpacity style={st.mCloseBtn} onPress={()=>setShowFilters(false)}><Ionicons name="close" size={22} color="#8E8E93"/></TouchableOpacity>
        <Text style={st.mTitle}>Filters</Text>
        <SaveBtn label="Reset" onPress={()=>setFilters({type:'All',department:'All',location:'All',remote:false,matchMin:0})}/>
      </View>
      <ScrollView style={{flex:1,padding:16}} showsVerticalScrollIndicator={false}>
        <View style={{marginBottom:16}}>
          <View style={{flexDirection:'row',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
            <Text style={{fontSize:14,fontWeight:'700',color:'#1C1C1E'}}>Remote Only</Text>
            <Switch value={filters.remote} onValueChange={v=>setFilters(p=>({...p,remote:v}))} trackColor={{false:'#E5E7EB',true:'#C4B5FD'}} thumbColor={filters.remote?'#667eea':'#FFF'}/>
          </View>
        </View>
        {[{l:'Work Type',k:'type',opts:['All','Virtual','Hybrid','In-Person']},
          {l:'Department',k:'department',opts:['All','Engineering','Design','Marketing','Data Science','Product']},
          {l:'Duration',k:'duration',opts:['All','8 weeks','10 weeks','12 weeks','16 weeks']},
        ].map(f=>(
          <View key={f.k} style={{marginBottom:16}}>
            <Text style={{fontSize:14,fontWeight:'700',color:'#1C1C1E',marginBottom:10}}>{f.l}</Text>
            <View style={{gap:6}}>
              {f.opts.map(opt=>(
                <TouchableOpacity key={opt} style={{flexDirection:'row',justifyContent:'space-between',alignItems:'center',paddingVertical:12,paddingHorizontal:14,borderRadius:10,backgroundColor:filters[f.k]===opt?'#EEF2FF':'#F8F9FA',borderWidth:1,borderColor:filters[f.k]===opt?'#C4B5FD':'#E5E7EB'}}
                  onPress={()=>setFilters(p=>({...p,[f.k]:opt}))}>
                  <Text style={{fontSize:14,color:filters[f.k]===opt?'#667eea':'#374151',fontWeight:filters[f.k]===opt?'600':'400'}}>{opt}</Text>
                  {filters[f.k]===opt&&<Ionicons name="checkmark" size={16} color="#667eea"/>}
                </TouchableOpacity>
              ))}
            </View>
          </View>
        ))}
      </ScrollView>
      <View style={{padding:16,borderTopWidth:1,borderTopColor:'#E5E5EA'}}>
        <TouchableOpacity style={{backgroundColor:'#667eea',borderRadius:12,paddingVertical:14,alignItems:'center'}} onPress={()=>setShowFilters(false)}>
          <Text style={{color:'#FFF',fontWeight:'700',fontSize:15}}>Show {filteredPrograms.length} Results</Text>
        </TouchableOpacity>
      </View>
    </View>
  );

  const renderEditProfileModal = () => {
    const fields=[
      {l:'Full Name',k:'name',kb:'default'},{l:'Email',k:'email',kb:'email-address'},
      {l:'Phone',k:'phone',kb:'phone-pad'},{l:'University',k:'university',kb:'default'},
      {l:'Major',k:'major',kb:'default'},{l:'Year',k:'year',kb:'default'},
      {l:'GPA',k:'gpa',kb:'numeric'},{l:'Availability',k:'availability',kb:'default'},
      {l:'Bio',k:'bio',multi:true},{l:'Portfolio URL',k:'portfolio',kb:'url'},
      {l:'LinkedIn URL',k:'linkedin',kb:'url'},{l:'GitHub URL',k:'github',kb:'url'},
    ];
    return (
      <KeyboardAvoidingView style={{flex:1}} behavior={Platform.OS==='ios'?'padding':'height'}>
        <ModalShell title="Edit Profile" onClose={()=>setShowModal(false)}
          rightEl={<SaveBtn label={savingForm?'Saving…':'Save'} onPress={saveProfile} disabled={savingForm}/>}>
          <ScrollView style={{flex:1,padding:16}} showsVerticalScrollIndicator={false} keyboardShouldPersistTaps="handled">
            {fields.map(f=>(
              <View key={f.k} style={{marginBottom:14}}>
                <Text style={st.formLabel}>{f.l}</Text>
                <TextInput style={[st.formInput,f.multi&&st.textArea]}
                  value={toS(editProfile[f.k]??studentData.profile[f.k])}
                  multiline={!!f.multi} numberOfLines={f.multi?3:1}
                  onChangeText={v=>setEditProfile(p=>({...p,[f.k]:v}))}
                  placeholder={`Enter ${String(f.l ?? '').toLowerCase()}`} placeholderTextColor="#C7C7CC"
                  keyboardType={f.kb||'default'}/>
              </View>
            ))}
            <View style={{marginBottom:14}}>
              <Text style={st.formLabel}>Skills (comma-separated)</Text>
              <TextInput style={st.formInput}
                value={(editProfile.skills||studentData.profile.skills||[]).join(', ')}
                onChangeText={v=>setEditProfile(p=>({...p,skills:v.split(',').map(x=>x.trim()).filter(Boolean)}))}
                placeholder="JavaScript, React, Python…" placeholderTextColor="#C7C7CC"/>
            </View>
            <View style={{marginBottom:14}}>
              <Text style={st.formLabel}>Interests (comma-separated)</Text>
              <TextInput style={st.formInput}
                value={(editProfile.interests||studentData.profile.interests||[]).join(', ')}
                onChangeText={v=>setEditProfile(p=>({...p,interests:v.split(',').map(x=>x.trim()).filter(Boolean)}))}
                placeholder="Web Development, AI/ML…" placeholderTextColor="#C7C7CC"/>
            </View>
            <View style={{height:20}}/>
          </ScrollView>
        </ModalShell>
      </KeyboardAvoidingView>
    );
  };

  const renderCompareModal = () => {
    if(compareList.length<2) return null;
    const rows=[{l:'Stipend',fn:p=>p.stipend},{l:'Duration',fn:p=>p.duration},{l:'Type',fn:p=>p.type},
      {l:'Match',fn:p=>`${p.matchScore}%`},{l:'Rating',fn:p=>`${p.rating}\u2B50`},
      {l:'Applicants',fn:p=>`${p.applicants}`},{l:'Department',fn:p=>p.department}];
    return (
      <View style={{flex:1,backgroundColor:'#FFF'}}>
        <View style={st.mHdr}>
          <TouchableOpacity style={st.mCloseBtn} onPress={()=>setShowCompare(false)}><Ionicons name="close" size={22} color="#8E8E93"/></TouchableOpacity>
          <Text style={st.mTitle}>Compare Internships</Text>
          <View style={{width:36}}/>
        </View>
        <ScrollView style={{flex:1,padding:16}} showsVerticalScrollIndicator={false}>
          <View style={{flexDirection:'row',paddingBottom:12,borderBottomWidth:1,borderBottomColor:'#F3F4F6'}}>
            <View style={{width:88}}/>
            {compareList.map(p=>(
              <View key={p.id} style={{flex:1,alignItems:'center',paddingHorizontal:4}}>
                <Image source={{uri:p.logo}} style={{width:34,height:34,borderRadius:8,marginBottom:4}}/>
                <Text style={{fontSize:11,fontWeight:'700',color:'#1C1C1E',textAlign:'center',lineHeight:14}} numberOfLines={2}>{p.title}</Text>
                <Text style={{fontSize:10,color:'#8E8E93',textAlign:'center',marginTop:1}}>{p.company}</Text>
              </View>
            ))}
          </View>
          {rows.map(row=>(
            <View key={row.l} style={{flexDirection:'row',paddingVertical:10,borderBottomWidth:1,borderBottomColor:'#F8F9FA',alignItems:'center'}}>
              <Text style={{width:88,fontSize:12,color:'#8E8E93',fontWeight:'600'}}>{row.l}</Text>
              {compareList.map(p=><Text key={p.id} style={{flex:1,fontSize:13,fontWeight:'600',color:'#374151',textAlign:'center'}}>{row.fn(p)}</Text>)}
            </View>
          ))}
          <View style={{flexDirection:'row',gap:8,marginTop:16}}>
            <View style={{width:88}}/>
            {compareList.map(p=>(
              <TouchableOpacity key={p.id} style={[st.applyBtn,{flex:1,justifyContent:'center'}]} onPress={()=>{setShowCompare(false);openApply(p);}}>
                <Text style={st.applyBtnT}>{appliedIds.has(p.id)?'Applied':'Apply'}</Text>
              </TouchableOpacity>
            ))}
          </View>
        </ScrollView>
      </View>
    );
  };

  const renderModal = () => {
    switch(modalType) {
      case 'detail':       return renderDetailModal();
      case 'apply':        return renderApplyModal();
      case 'appDetail':    return renderAppDetailModal();
      case 'notifications':return renderNotifModal();
      case 'editProfile':  return renderEditProfileModal();
      default:             return null;
    }
  };

  const renderContent = () => {
    switch(activeTab) {
      case 'discover':     return renderDiscover();
      case 'applications': return renderApplications();
      case 'saved':        return renderSaved();
      case 'profile':      return renderProfile();
      case 'analytics':    return renderAnalytics();
      default:             return renderDiscover();
    }
  };

  return (
    <SafeAreaView style={st.container}>
      {renderHeader()}
      {renderTabBar()}
      {renderContent()}

      {renderImageEditor()}
      <Modal visible={showModal}    animationType="slide" presentationStyle="pageSheet" onRequestClose={()=>setShowModal(false)}>{renderModal()}</Modal>
      <Modal visible={showFilters}  animationType="slide" presentationStyle="pageSheet" onRequestClose={()=>setShowFilters(false)}>{renderFiltersModal()}</Modal>
      <Modal visible={showCompare}  animationType="slide" presentationStyle="pageSheet" onRequestClose={()=>setShowCompare(false)}>{renderCompareModal()}</Modal>
    </SafeAreaView>
  );
}

const st = StyleSheet.create({
  container:    {flex:1,backgroundColor:'#F8F9FA'},
  header:       {paddingBottom:14},
  hdrRow:       {flexDirection:'row',alignItems:'center',paddingHorizontal:16,paddingTop:10,gap:8},
  hdrBtn:       {width:38,height:38,borderRadius:12,backgroundColor:'rgba(255,255,255,0.2)',justifyContent:'center',alignItems:'center'},
  hdrTitle:     {fontSize:18,fontWeight:'700',color:'#FFF'},
  hdrSub:       {fontSize:11,color:'rgba(255,255,255,0.8)',marginTop:1},
  badge:        {position:'absolute',top:-4,right:-4,backgroundColor:'#EF4444',borderRadius:8,minWidth:16,height:16,justifyContent:'center',alignItems:'center',paddingHorizontal:3},
  badgeT:       {fontSize:9,fontWeight:'700',color:'#FFF'},
  compRow:      {paddingHorizontal:16,paddingTop:8,paddingBottom:4},
  compLabel:    {fontSize:11,color:'rgba(255,255,255,0.7)',marginBottom:4},
  compBar:      {height:4,backgroundColor:'rgba(255,255,255,0.25)',borderRadius:2,overflow:'hidden'},
  compFill:     {height:4,backgroundColor:'#6EE7B7',borderRadius:2},
  tabBar:       {flexDirection:'row',backgroundColor:'#FFF',borderBottomWidth:1,borderBottomColor:'#E5E5EA'},
  tabItem:      {flex:1,paddingVertical:10,alignItems:'center',justifyContent:'center'},
  tabActive:    {borderBottomWidth:2,borderBottomColor:'#667eea'},
  tabText:      {fontSize:10,color:'#8E8E93',marginTop:3,fontWeight:'500'},
  tabTextActive:{color:'#667eea',fontWeight:'600'},
  tabBadge:     {position:'absolute',top:-6,right:-8,backgroundColor:'#EF4444',borderRadius:8,minWidth:14,height:14,justifyContent:'center',alignItems:'center',paddingHorizontal:2},
  tabBadgeT:    {fontSize:9,fontWeight:'700',color:'#FFF'},
  content:      {flex:1,backgroundColor:'#F8F9FA'},
  searchRow:    {flexDirection:'row',paddingHorizontal:16,paddingVertical:12,gap:10},
  searchBox:    {flex:1,flexDirection:'row',alignItems:'center',backgroundColor:'#FFF',borderRadius:12,paddingHorizontal:12,paddingVertical:11,gap:8,elevation:1,shadowColor:'#000',shadowOpacity:0.05,shadowRadius:3},
  searchIn:     {flex:1,fontSize:15,color:'#1C1C1E'},
  filterBtn:    {width:46,height:46,borderRadius:12,backgroundColor:'#FFF',justifyContent:'center',alignItems:'center',elevation:1},
  filterBtnOn:  {backgroundColor:'#EEF2FF',borderWidth:1,borderColor:'#C4B5FD'},
  sortRow:      {paddingHorizontal:16,paddingBottom:8,gap:8},
  sortChip:     {paddingHorizontal:12,paddingVertical:6,borderRadius:16,backgroundColor:'#FFF',borderWidth:1,borderColor:'#E5E7EB'},
  sortChipOn:   {backgroundColor:'#667eea',borderColor:'#667eea'},
  sortChipT:    {fontSize:12,color:'#374151',fontWeight:'500'},
  sortChipTOn:  {color:'#FFF'},
  sectionHdr:   {fontSize:14,fontWeight:'700',color:'#374151',paddingHorizontal:16,marginBottom:8},
  coCard:       {width:88,backgroundColor:'#FFF',borderRadius:12,padding:10,alignItems:'center',elevation:1,borderWidth:1,borderColor:'#F3F4F6'},
  coLogo:       {width:38,height:38,borderRadius:8,marginBottom:5},
  coName:       {fontSize:10,fontWeight:'700',color:'#374151',textAlign:'center'},
  coOpenings:   {fontSize:9,color:'#8E8E93',marginTop:1},
  cmpBar:       {flexDirection:'row',alignItems:'center',gap:8,backgroundColor:'#667eea',marginHorizontal:16,marginBottom:8,borderRadius:10,padding:10},
  cmpBarT:      {flex:1,color:'#FFF',fontWeight:'600',fontSize:13},
  resultsCount: {fontSize:12,color:'#8E8E93',paddingHorizontal:16,marginBottom:6},
  progCard:     {backgroundColor:'#FFF',marginHorizontal:16,marginBottom:14,borderRadius:16,padding:16,elevation:2,shadowColor:'#000',shadowOpacity:0.07,shadowRadius:8},
  matchPill:    {position:'absolute',top:12,right:12,backgroundColor:'#EEF2FF',borderRadius:8,paddingHorizontal:8,paddingVertical:3},
  matchPillT:   {fontSize:11,fontWeight:'700',color:'#667eea'},
  cardTop:      {flexDirection:'row',marginBottom:10,gap:10},
  logo:         {width:46,height:46,borderRadius:10},
  progTitle:    {fontSize:15,fontWeight:'700',color:'#1C1C1E'},
  progCo:       {fontSize:13,color:'#8E8E93',marginTop:1},
  metaRow:      {flexDirection:'row',flexWrap:'wrap',gap:6,marginTop:5},
  metaItem:     {flexDirection:'row',alignItems:'center',gap:3},
  metaT:        {fontSize:11,color:'#8E8E93'},
  iconBtn:      {width:30,height:30,borderRadius:8,backgroundColor:'#F8F9FA',justifyContent:'center',alignItems:'center'},
  progDesc:     {fontSize:13,color:'#6B7280',lineHeight:18,marginBottom:10},
  skillsRow:    {flexDirection:'row',flexWrap:'wrap',gap:6,marginBottom:10},
  skillChip:    {backgroundColor:'#EEF2FF',borderRadius:8,paddingHorizontal:8,paddingVertical:3},
  skillChipT:   {fontSize:11,color:'#667eea',fontWeight:'600'},
  more:         {fontSize:11,color:'#8E8E93',alignSelf:'center'},
  cardFoot:     {flexDirection:'row',justifyContent:'space-between',alignItems:'center'},
  statT:        {fontSize:12,color:'#6B7280'},
  typePill:     {borderRadius:6,paddingHorizontal:6,paddingVertical:2,marginLeft:4},
  tpGreen:      {backgroundColor:'#D1FAE5'},
  tpBlue:       {backgroundColor:'#DBEAFE'},
  tpOrange:     {backgroundColor:'#FEF3C7'},
  typePillT:    {fontSize:10,fontWeight:'700',color:'#374151'},
  cmpBtn:       {width:30,height:30,borderRadius:8,backgroundColor:'#EEF2FF',justifyContent:'center',alignItems:'center',borderWidth:1,borderColor:'#C4B5FD'},
  cmpBtnOn:     {backgroundColor:'#667eea'},
  applyBtn:     {backgroundColor:'#667eea',borderRadius:10,paddingHorizontal:14,paddingVertical:8,justifyContent:'center',alignItems:'center'},
  applyBtnT:    {fontSize:13,fontWeight:'700',color:'#FFF'},
  appliedPill:  {backgroundColor:'#D1FAE5',borderRadius:10,paddingHorizontal:10,paddingVertical:6},
  appliedPillT: {fontSize:12,fontWeight:'700',color:'#059669'},
  compactCard:  {flexDirection:'row',alignItems:'center',backgroundColor:'#FFF',marginHorizontal:16,marginBottom:8,borderRadius:12,padding:12,gap:10,elevation:1},
  compactLogo:  {width:38,height:38,borderRadius:8},
  compactTitle: {fontSize:14,fontWeight:'700',color:'#1C1C1E'},
  compactCo:    {fontSize:12,color:'#8E8E93',marginTop:1},
  appSummary:   {flexDirection:'row',backgroundColor:'#FFF',margin:16,borderRadius:14,padding:14,elevation:1,gap:4},
  appStatCard:  {flex:1,alignItems:'center'},
  appStatNum:   {fontSize:22,fontWeight:'900'},
  appStatLbl:   {fontSize:10,color:'#8E8E93',fontWeight:'500',marginTop:1},
  appCard:      {backgroundColor:'#FFF',borderRadius:14,padding:16,marginBottom:12,elevation:1,borderLeftWidth:4},
  appTop:       {flexDirection:'row',marginBottom:12},
  appTitle:     {fontSize:15,fontWeight:'700',color:'#1C1C1E'},
  appCo:        {fontSize:13,color:'#8E8E93',marginTop:2},
  appDate:      {fontSize:11,color:'#C7C7CC',marginTop:2},
  statusBadge:  {borderRadius:10,paddingHorizontal:10,paddingVertical:5,alignSelf:'flex-start',marginLeft:8},
  statusT:      {fontSize:11,fontWeight:'700'},
  progressLabel:{fontSize:12,color:'#6B7280',fontWeight:'600'},
  progBar:      {height:8,backgroundColor:'#F3F4F6',borderRadius:4,overflow:'hidden'},
  progFill:     {height:8,borderRadius:4,backgroundColor:'#667eea'},
  appActionBtn: {flex:1,flexDirection:'row',justifyContent:'center',alignItems:'center',gap:4,paddingVertical:8,borderRadius:8,backgroundColor:'#EEF2FF'},
  appActionBtnT:{fontSize:13,fontWeight:'600',color:'#667eea'},
  profSection:  {backgroundColor:'#FFF',marginHorizontal:14,marginTop:12,borderRadius:14,padding:16,elevation:1},
  secTitle:     {fontSize:15,fontWeight:'700',color:'#1C1C1E',marginBottom:10},
  profName:     {fontSize:22,fontWeight:'800',color:'#FFF',textAlign:'center',marginBottom:2},
  profStats:    {flexDirection:'row',backgroundColor:'rgba(255,255,255,0.15)',borderRadius:14,padding:14,marginTop:14,gap:4},
  avatar:       {width:80,height:80,borderRadius:40,borderWidth:3,borderColor:'rgba(255,255,255,0.5)',marginBottom:10},
  editProfBtn:  {flexDirection:'row',alignItems:'center',gap:6,backgroundColor:'#FFF',borderRadius:10,paddingHorizontal:14,paddingVertical:8,marginTop:14},
  aCard:        {backgroundColor:'#FFF',borderRadius:14,padding:16,marginBottom:14,borderWidth:1,borderColor:'#F3F4F6',elevation:1},
  aCardTitle:   {fontSize:15,fontWeight:'700',color:'#1C1C1E',marginBottom:12},
  empty:        {alignItems:'center',paddingVertical:48,paddingHorizontal:24},
  emptyTitle:   {fontSize:17,fontWeight:'700',color:'#374151',marginTop:12},
  emptyText:    {fontSize:13,color:'#8E8E93',textAlign:'center',marginTop:6,lineHeight:20},
  emptyBtn:     {marginTop:16,backgroundColor:'#667eea',borderRadius:10,paddingHorizontal:20,paddingVertical:10},
  emptyBtnT:    {fontSize:14,fontWeight:'700',color:'#FFF'},
  mHdr:         {flexDirection:'row',alignItems:'center',paddingHorizontal:16,paddingVertical:14,borderBottomWidth:1,borderBottomColor:'#E5E5EA'},
  mCloseBtn:    {width:36,height:36,borderRadius:10,backgroundColor:'#F8F9FA',justifyContent:'center',alignItems:'center'},
  mTitle:       {flex:1,textAlign:'center',fontSize:16,fontWeight:'700',color:'#1C1C1E',marginHorizontal:8},
  mSaveBtn:     {backgroundColor:'#667eea',borderRadius:8,paddingHorizontal:12,paddingVertical:7},
  mSaveBtnT:    {color:'#FFF',fontWeight:'700',fontSize:13},
  shareBtn:     {width:48,height:48,borderRadius:12,backgroundColor:'#EEF2FF',justifyContent:'center',alignItems:'center',borderWidth:1,borderColor:'#C4B5FD'},
  applyModalBtn:{flex:1,backgroundColor:'#667eea',borderRadius:12,paddingVertical:14,justifyContent:'center',alignItems:'center'},
  applyModalBtnT:{color:'#FFF',fontWeight:'700',fontSize:15},
  formLabel:    {fontSize:13,fontWeight:'600',color:'#374151',marginBottom:6},
  formInput:    {borderWidth:1,borderColor:'#E5E7EB',borderRadius:10,paddingHorizontal:12,paddingVertical:11,fontSize:14,color:'#1C1C1E',backgroundColor:'#FAFAFA'},
  textArea:     {minHeight:88,textAlignVertical:'top'},
});
